import React, { useState, useEffect, useMemo } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { calculateNumerology, NumerologyAnalysis } from '../utils/numerologyEngine';
import { LAGNA_INSIGHTS, getAuspiciousDirection } from '../utils/basicProfileInterpretations';
import {
  Sparkles,
  Hash,
  Palette,
  Gem,
  Calendar,
  Compass,
  ArrowRight,
  Info,
  X,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Shield,
  Sun,
  Moon,
  Ban,
  MinusCircle,
  Car,
  Wallet,
  Home,
  Building2,
  Briefcase,
  Smartphone,
  PenTool
} from 'lucide-react';

interface AuspiciousEssentialsBarProps {
  kundali: CompleteKundali;
  lang: Language;
  onOpenNumerology?: () => void;
  onOpenGemstones?: () => void;
  className?: string;
  variant?: 'compact' | 'full' | 'banner';
}

// Color palette mapping helper for dynamic preview dots
export function getColorHex(colorName: string): string {
  const c = (colorName || '').toLowerCase();
  if (c.includes('black') || c.includes('काला')) return '#18181B';
  if (c.includes('navy') || c.includes('गहरा नीला') || c.includes('dark blue')) return '#1E3A8A';
  if (c.includes('slate') || c.includes('charcoal') || c.includes('कोयला')) return '#334155';
  if (c.includes('grey') || c.includes('gray') || c.includes('स्लेटी') || c.includes('राख')) return '#64748B';
  if (c.includes('brown') || c.includes('भूरा')) return '#78350F';
  if (c.includes('maroon') || c.includes('मैरून')) return '#881337';
  if (c.includes('crimson') || c.includes('सिंदूरी') || c.includes('रक्त')) return '#991B1B';
  if (c.includes('mustard') || c.includes('सरसों')) return '#CA8A04';
  if (c.includes('ochre') || c.includes('गेरुआ')) return '#B45309';
  if (c.includes('red') || c.includes('लाल')) return '#EF4444';
  if (c.includes('coral') || c.includes('मूंगा')) return '#F87171';
  if (c.includes('yellow') || c.includes('पीला') || c.includes('amber')) return '#EAB308';
  if (c.includes('gold') || c.includes('सुनहरा')) return '#F59E0B';
  if (c.includes('orange') || c.includes('नारंगी') || c.includes('saffron') || c.includes('केसरिया')) return '#F97316';
  if (c.includes('green') || c.includes('हरा')) return '#10B981';
  if (c.includes('emerald') || c.includes('पन्ना') || c.includes('jade')) return '#059669';
  if (c.includes('blue') || c.includes('नीला')) return '#3B82F6';
  if (c.includes('turquoise') || c.includes('फिरोजा')) return '#06B6D4';
  if (c.includes('white') || c.includes('सफेद') || c.includes('silver') || c.includes('चांदी')) return '#F8FAFC';
  if (c.includes('pink') || c.includes('गुलाबी') || c.includes('rose')) return '#EC4899';
  if (c.includes('cream') || c.includes('क्रीम')) return '#FEF3C7';
  if (c.includes('copper') || c.includes('ताम्र') || c.includes('तांबई')) return '#B45309';
  if (c.includes('purple') || c.includes('बैंगनी') || c.includes('violet') || c.includes('जामुनी')) return '#8B5CF6';
  return '#B58A4A';
}

export interface ColorItem {
  name: string;
  hex: string;
}

export interface ColorCategoryData {
  items: ColorItem[];
  summary: string;
  advice: string;
  rationale: string;
}

export interface ColorTaxonomyResult {
  lucky: ColorCategoryData;
  neutral: ColorCategoryData;
  avoid: ColorCategoryData;
  remedyIfWorn: string;
}


// Triadic Color Taxonomy: Lucky, Neutral, and Avoid Colors
export function getColorTaxonomy(
  lagnaSign: string,
  moolank: number,
  lang: Language = 'en',
  customVedicColor?: string
): ColorTaxonomyResult {
  const isHi = lang === 'hi';
  const sign = (lagnaSign || 'Aries').trim();

  // Lagna based triadic color harmonizations (Parashara Jyotish)
  const LAGNA_COLOR_MAP: Record<string, {
    luckyEn: string[]; luckyHi: string[];
    neutralEn: string[]; neutralHi: string[];
    avoidEn: string[]; avoidHi: string[];
    remedyEn: string; remedyHi: string;
    rationaleEn: string; rationaleHi: string;
  }> = {
    Aries: {
      luckyEn: ['Coral Red', 'Saffron Orange', 'Golden Yellow', 'Warm Amber'],
      luckyHi: ['मूंगा लाल', 'केसरिया नारंगी', 'सुनहरा पीला', 'अंबर'],
      neutralEn: ['Off-White', 'Cream', 'Light Pink'],
      neutralHi: ['हल्का सफेद', 'क्रीम', 'हल्का गुलाबी'],
      avoidEn: ['Jet Black', 'Dark Charcoal', 'Dark Navy Blue'],
      avoidHi: ['गहरा काला', 'कोयला स्लेटी', 'गहरा नेवी ब्लू'],
      remedyEn: 'If dark black or navy blue is required, carry a coral red or saffron handkerchief in your pocket to neutralize Saturnian suppression of Mars.',
      remedyHi: 'यदि काला या गहरा नीला पहनना अनिवार्य हो, तो जेब में मूंगा लाल या केसरिया रुमाल रखें ताकि मंगल का तेज बना रहे।',
      rationaleEn: 'Aries is ruled by fiery Mars. Saturn (black/dark blue) is a natural adversary and brings inertia and mental fatigue.',
      rationaleHi: 'मेष लग्न का स्वामी मंगल (अग्नि तत्व) है। शनि (काला/गहरा नीला) विरोधी होने से ऊर्जा में शिथिलता व अवरोध लाता है।'
    },
    Taurus: {
      luckyEn: ['Pearl White', 'Diamond Silver', 'Pastel Pink', 'Emerald Green', 'Turquoise'],
      luckyHi: ['मोती सफेद', 'चांदी', 'हल्का गुलाबी', 'पन्ना हरा', 'फिरोजा'],
      neutralEn: ['Cream', 'Sky Blue', 'Light Peach'],
      neutralHi: ['क्रीम', 'आसमानी नीला', 'हल्का पीच'],
      avoidEn: ['Blood Red', 'Bright Fiery Scarlet', 'Dark Orange'],
      avoidHi: ['खूनी लाल', 'भड़कीला सिंदूरी लाल', 'गहरा नारंगी'],
      remedyEn: 'If wearing bright red is mandatory, balance it with a pearl white scarf, silver accessory, or diamond pendant.',
      remedyHi: 'यदि लाल रंग पहनना अनिवार्य हो, तो साथ में सफेद स्कार्फ, चांदी का आभूषण या मोती धारण कर संतुलन बनाएं।',
      rationaleEn: 'Taurus is ruled by luxurious Venus. Fiery Martian reds overstimulate the system, causing restlessness and conflicts.',
      rationaleHi: 'वृषभ लग्न का स्वामी शुक्र है। मंगल का उग्र लाल रंग शांति और सौम्यता में बाधा पहुंचाता है।'
    },
    Gemini: {
      luckyEn: ['Emerald Green', 'Mint Green', 'Light Sky Blue', 'Silver White'],
      luckyHi: ['पन्ना हरा', 'पिस्ता हरा', 'हल्का आसमानी', 'चांदी सफेद'],
      neutralEn: ['Soft Yellow', 'Cream', 'Pastel Grey'],
      neutralHi: ['हल्का पीला', 'क्रीम', 'हल्का स्लेटी'],
      avoidEn: ['Deep Maroon', 'Dark Fiery Crimson', 'Dark Muddy Brown'],
      avoidHi: ['गहरा मैरून', 'उग्र सिंदूरी', 'मटमैला भूरा'],
      remedyEn: 'Wear a green jade or emerald ring and carry a mint green accessory if forced to wear fiery deep crimson.',
      remedyHi: 'यदि गहरा लाल या मैरून पहनना पड़े, तो हरा रुमाल पास रखें या पन्ना/ओपल धारण करें।',
      rationaleEn: 'Gemini is ruled by intellectual Mercury. Fiery Martian crimson scatters focus and sparks nervous anxiety.',
      rationaleHi: 'मिथुन लग्न का स्वामी बुध है। मंगल का तामसिक लाल रंग बौद्धिक एकाग्रता को खंडित करता है।'
    },
    Cancer: {
      luckyEn: ['Pearl White', 'Moon Silver', 'Cream', 'Sea Green', 'Pastel Rose'],
      luckyHi: ['मोती सफेद', 'चांदी', 'क्रीम', 'हल्का हरा', 'गुलाबी'],
      neutralEn: ['Light Yellow', 'Sky Blue', 'Peach'],
      neutralHi: ['हल्का पीला', 'आसमानी नीला', 'पीच'],
      avoidEn: ['Jet Black', 'Dark Charcoal', 'Pitch Dark Navy Blue'],
      avoidHi: ['गहरा काला', 'कोयला स्लेटी', 'गहरा नेवी ब्लू'],
      remedyEn: 'Wear a natural pearl or silver bracelet if compelled to wear black/dark blue to preserve emotional equilibrium.',
      remedyHi: 'यदि काला पहनना अनिवार्य हो, तो चांदी का कड़ा या मोती धारण करें ताकि मानसिक शांति और सकारात्मकता बनी रहे।',
      rationaleEn: 'Cancer is ruled by receptive Moon. Saturnian blacks dampen sensitivity and induce melancholy.',
      rationaleHi: 'कर्क लग्न का स्वामी चंद्रमा है। शनि व राहु का काला रंग मन में अवसाद और नकारात्मक विचार पैदा करता है।'
    },
    Leo: {
      luckyEn: ['Sun Gold', 'Warm Amber', 'Saffron Orange', 'Ruby Red', 'Radiant White'],
      luckyHi: ['सुनहरा गोल्ड', 'अंबर', 'केसरिया नारंगी', 'माणिक्य लाल', 'उज्ज्वल सफेद'],
      neutralEn: ['Cream', 'Soft Rose', 'Warm Beige'],
      neutralHi: ['क्रीम', 'गुलाबी', 'हल्का बादामी'],
      avoidEn: ['Dull Pitch Black', 'Dark Blue', 'Cold Steel Grey'],
      avoidHi: ['धूमिल काला', 'गहरा नीला', 'ठंडा स्लेटी'],
      remedyEn: 'Keep a copper coin or ruby/sunstone pendant when wearing dark shades to channel solar vitality and authority.',
      remedyHi: 'यदि काला या नीला पहनना पड़े, तो तांबे का कड़ा या माणिक्य पहनें ताकि सूर्य का ओज और पराक्रम बना रहे।',
      rationaleEn: 'Leo is ruled by the royal Sun. Saturn (black/dark blue) is the Sun natural astronomical adversary.',
      rationaleHi: 'सिंह लग्न का स्वामी सूर्य है। शनि का काला-नीला रंग सूर्य के स्वाभाविक तेज और नेतृत्व गुण को दबाता है।'
    },
    Virgo: {
      luckyEn: ['Emerald Green', 'Forest Green', 'Ivory White', 'Turquoise', 'Mint'],
      luckyHi: ['पन्ना हरा', 'गहरा हरा', 'आइवरी सफेद', 'फिरोजा', 'पिस्ता'],
      neutralEn: ['Soft Sky Blue', 'Pale Cream', 'Light Taupe'],
      neutralHi: ['हल्का आसमानी', 'क्रीम', 'हल्का बादामी'],
      avoidEn: ['Blood Red', 'Fiery Scarlet', 'Blazing Crimson'],
      avoidHi: ['खूनी लाल', 'भड़कीला सिंदूरी', 'उग्र सुर्ख लाल'],
      remedyEn: 'Pair any unavoidable red garment with a prominent green or ivory scarf/pen.',
      remedyHi: 'यदि लाल रंग पहनना अनिवार्य हो, तो हरा पेन, पन्ना या आइवरी स्कार्फ अवश्य साथ रखें।',
      rationaleEn: 'Virgo is ruled by analytical Mercury. Fiery Martian reds create mental irritation and decision turbulence.',
      rationaleHi: 'कन्या लग्न का स्वामी बुध है। मंगल का लाल रंग वाणी में कठोरता और मानसिक तनाव लाता है।'
    },
    Libra: {
      luckyEn: ['Diamond White', 'Silver', 'Pastel Pink', 'Light Turquoise', 'Sky Blue'],
      luckyHi: ['उज्ज्वल सफेद', 'चांदी', 'गुलाबी', 'हल्का फिरोजा', 'आसमानी नीला'],
      neutralEn: ['Cream', 'Lilac', 'Soft Mint Green'],
      neutralHi: ['क्रीम', 'हल्का बैंगनी', 'हल्का हरा'],
      avoidEn: ['Muddy Mustard Yellow', 'Dull Mud Brown', 'Harsh Blood Red'],
      avoidHi: ['मटमैला पीला', 'धूमिल भूरा', 'कठोर खूनी लाल'],
      remedyEn: 'Carry a silver coin or clear quartz/diamond accessory to maintain harmonious Venusian grace.',
      remedyHi: 'यदि पीला या लाल पहनना पड़े, तो चांदी या स्फटिक की माला/अंगूठी पास रखें।',
      rationaleEn: 'Libra is ruled by graceful Venus. Muddy yellows and aggressive reds disrupt balance, charm, and partnership rapport.',
      rationaleHi: 'तुला लग्न का स्वामी शुक्र है। मटमैला पीला व उग्र लाल रंग सौहार्द और संतुलन में तनाव पैदा करता है।'
    },
    Scorpio: {
      luckyEn: ['Coral Red', 'Deep Crimson', 'Warm Copper', 'Golden Amber', 'Pearl White'],
      luckyHi: ['मूंगा लाल', 'गहरा सिंदूरी', 'तांबई', 'सुनहरा अंबर', 'मोती सफेद'],
      neutralEn: ['Ivory', 'Soft Rose', 'Warm Tan'],
      neutralHi: ['आइवरी', 'गुलाबी', 'हल्का बादामी'],
      avoidEn: ['Pitch Black', 'Dark Navy Blue', 'Bottle Green'],
      avoidHi: ['गहरा काला', 'गहरा नेवी ब्लू', 'गहरा बोतल हरा'],
      remedyEn: 'Tie a red mauli (sacred thread) or keep a copper piece in your pocket when wearing dark tones.',
      remedyHi: 'यदि काला या नेवी पहनना पड़े, तो कलाई पर लाल मौली बांधें या तांबे का सिक्का पास रखें।',
      rationaleEn: 'Scorpio is ruled by secretive Mars. Saturnian black and green drain transformative intuition and vitality.',
      rationaleHi: 'वृश्चिक लग्न का स्वामी मंगल है। काला और हरा रंग आत्मविश्वास और आत्मशक्ति को कम करता है।'
    },
    Sagittarius: {
      luckyEn: ['Golden Yellow', 'Warm Saffron', 'Amber', 'Cream', 'Radiant White'],
      luckyHi: ['सुनहरा पीला', 'केसरिया', 'अंबर', 'क्रीम', 'उज्ज्वल सफेद'],
      neutralEn: ['Light Orange', 'Soft Sky Blue', 'Peach'],
      neutralHi: ['हल्का नारंगी', 'आसमानी नीला', 'पीच'],
      avoidEn: ['Jet Black', 'Pitch Charcoal', 'Dark Navy Blue'],
      avoidHi: ['गहरा काला', 'कोयला स्लेटी', 'गहरा नेवी ब्लू'],
      remedyEn: 'Keep a yellow silk handkerchief or brass accessory when forced to wear dark shades.',
      remedyHi: 'यदि काला पहनना अनिवार्य हो, तो जेब में पीला रुमाल रखें या पीतल/सोने का छल्ला पहनें।',
      rationaleEn: 'Sagittarius is ruled by benevolent Jupiter. Saturnian blacks restrict optimism, wisdom, and auspicious luck.',
      rationaleHi: 'धनु लग्न का स्वामी गुरु है। शनि का काला रंग गुरु की कृपा, ज्ञान और भाग्य वृद्धि को सीमित करता है।'
    },
    Capricorn: {
      luckyEn: ['Royal Navy Blue', 'Steel Grey', 'Charcoal', 'Emerald Green', 'White'],
      luckyHi: ['रॉयल नेवी ब्लू', 'स्टील स्लेटी', 'कोयला रंग', 'पन्ना हरा', 'सफेद'],
      neutralEn: ['Black', 'Off-White', 'Muted Olive'],
      neutralHi: ['काला', 'हल्का सफेद', 'हल्का ऑलिव'],
      avoidEn: ['Blazing Scarlet Red', 'Fiery Orange', 'Bright Crimson'],
      avoidHi: ['उग्र भड़कीला लाल', 'उग्र नारंगी', 'सिंदूरी लाल'],
      remedyEn: 'Balance red with a navy blue coat or steel watch to prevent rash haste.',
      remedyHi: 'यदि लाल पहनना अनिवार्य हो, तो साथ में गहरा नीला ब्लेजर या स्टील घड़ी पहनकर संतुलन बनाएं।',
      rationaleEn: 'Capricorn is ruled by patient Saturn. Martian reds provoke unnecessary haste, anger, and friction.',
      rationaleHi: 'मकर लग्न का स्वामी शनि है। मंगल का लाल रंग उतावलापन और विवाद की स्थिति बनाता है।'
    },
    Aquarius: {
      luckyEn: ['Electric Sky Blue', 'Ultramarine Blue', 'Deep Violet', 'Silver', 'Pearl White'],
      luckyHi: ['आसमानी नीला', 'गहरा नीला', 'बैंगनी', 'चांदी', 'मोती सफेद'],
      neutralEn: ['Grey', 'Charcoal', 'Muted Green'],
      neutralHi: ['स्लेटी', 'कोयला रंग', 'हल्का हरा'],
      avoidEn: ['Blood Red', 'Bright Fiery Scarlet', 'Muddy Rust Brown'],
      avoidHi: ['खूनी लाल', 'भड़कीला सिंदूरी लाल', 'मटमैला भूरा'],
      remedyEn: 'Wear a silver chain or keep a blue/white accessory handy when wearing reds.',
      remedyHi: 'यदि लाल पहनना पड़े, तो चांदी की चेन या नीला रुमाल पास रखें।',
      rationaleEn: 'Aquarius is ruled by progressive Saturn/Rahu. Blazing reds trigger agitation and scatter visionary focus.',
      rationaleHi: 'कुंभ लग्न का स्वामी शनि है। भड़कीला लाल रंग मानसिक शांति और एकाग्रता को बाधित करता है।'
    },
    Pisces: {
      luckyEn: ['Golden Yellow', 'Canary Yellow', 'Warm Saffron', 'Pearl White', 'Sea Foam'],
      luckyHi: ['सुनहरा पीला', 'हल्का पीला', 'केसरिया', 'मोती सफेद', 'हल्का हरा'],
      neutralEn: ['Rose Pink', 'Cream', 'Pastel Coral'],
      neutralHi: ['गुलाबी', 'क्रीम', 'हल्का मूंगा'],
      avoidEn: ['Jet Black', 'Dark Charcoal Grey', 'Dark Navy Blue'],
      avoidHi: ['गहरा काला', 'कोयला स्लेटी', 'गहरा नेवी ब्लू'],
      remedyEn: 'Carry a yellow topaz or saffron thread to maintain spiritual intuition and peace when wearing dark tones.',
      remedyHi: 'यदि काला या नेवी पहनना अनिवार्य हो, तो पीला रुमाल या हल्दी की गांठ साथ रखें।',
      rationaleEn: 'Pisces is ruled by spiritual Jupiter. Saturnian blacks cloud intuition and spiritual serenity.',
      rationaleHi: 'मीन लग्न का स्वामी गुरु है। काला रंग अंतर्ज्ञान और आध्यात्मिक ऊर्जा को अवरुद्ध करता है।'
    }
  };

  const item = LAGNA_COLOR_MAP[sign] || LAGNA_COLOR_MAP.Aries;

  // Add custom Vedic color if provided and not already present
  const luckyList = isHi ? [...item.luckyHi] : [...item.luckyEn];
  if (customVedicColor && !luckyList.includes(customVedicColor)) {
    luckyList.unshift(customVedicColor);
  }

  const luckyItems: ColorItem[] = luckyList.map((name) => ({
    name,
    hex: getColorHex(name)
  }));

  const neutralList = isHi ? item.neutralHi : item.neutralEn;
  const neutralItems: ColorItem[] = neutralList.map((name) => ({
    name,
    hex: getColorHex(name)
  }));

  const avoidList = isHi ? item.avoidHi : item.avoidEn;
  const avoidItems: ColorItem[] = avoidList.map((name) => ({
    name,
    hex: getColorHex(name)
  }));

  return {
    lucky: {
      items: luckyItems,
      summary: luckyList.slice(0, 3).join(', '),
      advice: isHi
        ? 'शुभ वार्ता, अनुबंध, इंटरव्यू व मुख्य अवसरों पर ' + luckyList.slice(0, 2).join(' या ') + ' रंग धारण करें।'
        : 'Wear ' + luckyList.slice(0, 2).join(' or ') + ' for pivotal negotiations, interviews, and auspicious ventures.',
      rationale: isHi ? item.rationaleHi : item.rationaleEn
    },
    neutral: {
      items: neutralItems,
      summary: neutralList.slice(0, 3).join(', '),
      advice: isHi
        ? 'दैनिक सामान्य कार्य, घर व नियमित दिनचर्या में ' + neutralList.slice(0, 2).join(' व ') + ' रंग संतुलित ऊर्जा देते हैं।'
        : 'Safe and balanced for routine chores, everyday office casuals, and neutral day-to-day balance.',
      rationale: isHi
        ? 'ये रंग न तो ग्रह को उत्तेजित करते हैं न ही अवरोध पैदा करते हैं; स्वाभाविक संतुलन बनाए रखते हैं।'
        : 'These neutral hues create neither friction nor excessive stimulation; ideal for equilibrium.'
    },
    avoid: {
      items: avoidItems,
      summary: avoidList.slice(0, 2).join(', '),
      advice: isHi
        ? 'अति महत्वपूर्ण फैसलों, विवाह वार्ताओं व शुभ आरंभ में ' + avoidList.slice(0, 2).join(' व ') + ' रंगों से बचें।'
        : 'Avoid wearing ' + avoidList.slice(0, 2).join(' and ') + ' for signature moments, key exams, or major investments.',
      rationale: isHi ? item.rationaleHi : item.rationaleEn
    },
    remedyIfWorn: isHi ? item.remedyHi : item.remedyEn
  };
}

export type LifeDomainKey = 'vehicle' | 'wallet' | 'home' | 'office' | 'branding' | 'gadgets' | 'signature';

export interface DomainColorItem {
  id: LifeDomainKey;
  iconName: string;
  titleEn: string;
  titleHi: string;
  bhavaEn: string;
  bhavaHi: string;
  recommendedColorsEn: string[];
  recommendedColorsHi: string[];
  avoidColorsEn: string[];
  avoidColorsHi: string[];
  guidanceEn: string;
  guidanceHi: string;
  astrologicalSecretEn: string;
  astrologicalSecretHi: string;
}

// 7 Practical Life Domain Recommendations based on Lagna & Moolank
export function getLifeDomainColorAdvice(lagnaSign: string, moolank: number, lang: Language): DomainColorItem[] {
  const isHi = lang === 'hi';
  const sign = (lagnaSign || 'Aries').trim();

  // Mapping domain profiles based on Vedic 4th House (vehicles/home), 2nd/11th House (wallet), 10th House (office/brand)
  const DOMAIN_DATA: Record<string, {
    vehicleRecEn: string[]; vehicleRecHi: string[];
    vehicleAvoidEn: string[]; vehicleAvoidHi: string[];
    vehicleGuideEn: string; vehicleGuideHi: string;
    vehicleSecretEn: string; vehicleSecretHi: string;

    walletRecEn: string[]; walletRecHi: string[];
    walletAvoidEn: string[]; walletAvoidHi: string[];
    walletGuideEn: string; walletGuideHi: string;

    homeRecEn: string[]; homeRecHi: string[];
    homeAvoidEn: string[]; homeAvoidHi: string[];
    homeGuideEn: string; homeGuideHi: string;

    officeRecEn: string[]; officeRecHi: string[];
    officeAvoidEn: string[]; officeAvoidHi: string[];
    officeGuideEn: string; officeGuideHi: string;

    brandRecEn: string[]; brandRecHi: string[];
    brandAvoidEn: string[]; brandAvoidHi: string[];
    brandGuideEn: string; brandGuideHi: string;

    gadgetRecEn: string[]; gadgetRecHi: string[];
    gadgetAvoidEn: string[]; gadgetAvoidHi: string[];
    gadgetGuideEn: string; gadgetGuideHi: string;

    sigRecEn: string[]; sigRecHi: string[];
    sigAvoidEn: string[]; sigAvoidHi: string[];
    sigGuideEn: string; sigGuideHi: string;
  }> = {
    Aries: {
      vehicleRecEn: ['White', 'Silver', 'Pearl Grey', 'Fire Red', 'Amber'],
      vehicleRecHi: ['सफेद', 'चांदी', 'हल्का स्लेटी', 'मूंगा लाल', 'नारंगी'],
      vehicleAvoidEn: ['Jet Black', 'Dark Navy Blue'],
      vehicleAvoidHi: ['गहरा काला', 'गहरा नेवी ब्लू'],
      vehicleGuideEn: '4th house Cancer (Moon). White/Silver or Coral Red protects mechanical endurance, prevents road rashness and vehicle disputes.',
      vehicleGuideHi: 'चतुर्थ भाव कर्क (चंद्रमा)। सफेद, सिल्वर या मूंगा रंग वाहन सुख बढ़ाता है तथा दुर्घटना एवं चालान की आशंका कम करता है।',
      vehicleSecretEn: 'Keep a small silver Ganesha or peacock feather in the dashboard for planetary peace.',
      vehicleSecretHi: 'डैशबोर्ड में चांदी का छोटा सिक्का या मोरपंख रखने से राहु-शनि की बाधा शांत रहती है।',

      walletRecEn: ['Red Tan', 'Golden Amber', 'Warm Brown'],
      walletRecHi: ['तांबई लाल', 'सुनहरा अंबर', 'गहरा भूरा'],
      walletAvoidEn: ['Jet Black', 'Dark Blue'],
      walletAvoidHi: ['काला', 'गहरा नीला'],
      walletGuideEn: 'Stimulates energetic liquidity and prevents cash stagnation.',
      walletGuideHi: 'धन संचय व त्वरित नकदी प्रवाह को निरंतर सक्रिय रखता है।',

      homeRecEn: ['Cream', 'Soft Ivory', 'Light Peach', 'Off-White'],
      homeRecHi: ['क्रीम', 'सफेद', 'हल्का पीच', 'हल्का पीला'],
      homeAvoidEn: ['Dark Slate', 'Black Accent Walls'],
      homeAvoidHi: ['गहरा स्लेटी', 'काली दीवारें'],
      homeGuideEn: 'Ensures domestic tranquility and cools the Martian temperament at home.',
      homeGuideHi: 'घर में मानसिक शांति बनाए रखता है और मंगल के उग्र स्वभाव को शांत रखता है।',

      officeRecEn: ['Saffron', 'Warm Oak', 'Light Coral', 'Ivory'],
      officeRecHi: ['केसरिया', 'ओक वुड', 'कोरल', 'आइवरी'],
      officeAvoidEn: ['Dull Navy', 'Dark Charcoal'],
      officeAvoidHi: ['गहरा नेवी', 'कोयला स्लेटी'],
      officeGuideEn: 'Maintains high decision-making courage and team respect.',
      officeGuideHi: 'कार्यस्थल पर नेतृत्व क्षमता, तेजी से निर्णय व सहयोगियों में सम्मान बढ़ाता है।',

      brandRecEn: ['Bright Red', 'Gold', 'Royal Orange'],
      brandRecHi: ['चमकीला लाल', 'गोल्डन', 'केसरिया नारंगी'],
      brandAvoidEn: ['Dull Grey', 'Black/Blue combo'],
      brandAvoidHi: ['धूमिल ग्रे', 'काला-नीला'],
      brandGuideEn: 'Projects pioneering boldness, speed, and premium executive status.',
      brandGuideHi: 'ब्रांड में मौलिकता, गतिशीलता और अग्रणी छवि स्थापित करता है।',

      gadgetRecEn: ['Silver', 'White', 'Titanium Grey', 'Red Accents'],
      gadgetRecHi: ['सिल्वर', 'व्हाइट', 'टाइटेनियम ग्रे', 'लाल कवर'],
      gadgetAvoidEn: ['Pure Matte Black Case'],
      gadgetAvoidHi: ['मैट ब्लैक बैक कवर'],
      gadgetGuideEn: 'Protects against nervous exhaustion and smartphone screen anxiety.',
      gadgetGuideHi: 'मोबाइल विकिरण व मानसिक तनाव से सुरक्षा और एकाग्रता देता है।',

      sigRecEn: ['Royal Blue Ink', 'Green Ink'],
      sigRecHi: ['रॉयल ब्लू स्याही', 'हरी स्याही'],
      sigAvoidEn: ['Faded Black', 'Red Ink for banking'],
      sigAvoidHi: ['हल्की काली स्याही', 'बैंक कार्यों में लाल स्याही'],
      sigGuideEn: 'Green ink on official cheques/deeds stimulates Mercury-Mars wealth accumulation.',
      sigGuideHi: 'महत्वपूर्ण अनुबंधों पर हरी या नीली स्याही शुभ फल और समृद्धि देती है।'
    },
    Taurus: {
      vehicleRecEn: ['Pearl White', 'Metallic Silver', 'Turquoise Blue', 'Champagne'],
      vehicleRecHi: ['मोती सफेद', 'चांदी', 'फिरोजा नीला', 'शैंपेन रंग'],
      vehicleAvoidEn: ['Dark Blood Red', 'Bright Scarlet'],
      vehicleAvoidHi: ['गहरा खूनी लाल', 'भड़कीला सिंदूरी'],
      vehicleGuideEn: '4th house Leo (Sun). White/Silver or Champagne conveys Venusian luxury and long-term resale value.',
      vehicleGuideHi: 'चतुर्थ भाव सिंह (सूर्य)। सफेद, सिल्वर या शैंपेन रंग वाहन में दीर्घकालिक स्थायित्व और लग्जरी देता है।',
      vehicleSecretEn: 'Store a natural camphor tablet or rose essence sachet in the car for Venusian prosperity.',
      vehicleSecretHi: 'गाड़ी में कपूर या चंदन की खुशबू रखने से नकारात्मक ऊर्जा दूर रहती है।',

      walletRecEn: ['Emerald Green', 'Soft Tan', 'Metallic Silver'],
      walletRecHi: ['पन्ना हरा', 'मुलायम भूरा', 'चांदी रंग'],
      walletAvoidEn: ['Fiery Red', 'Bright Neon Orange'],
      walletAvoidHi: ['उग्र लाल', 'चमकीला नारंगी'],
      walletGuideEn: 'Attracts stable compounding wealth, retail abundance, and financial preservation.',
      walletGuideHi: 'आर्थिक संचय व अनावश्यक खर्चों पर नियंत्रण रखने में सहायक।',

      homeRecEn: ['Soft Ivory', 'Powder Blue', 'Pastel Pink', 'Sage Green'],
      homeRecHi: ['आइवरी', 'हल्का आसमानी', 'गुलाबी', 'हल्का हरा'],
      homeAvoidEn: ['Dark Crimson', 'Rust Orange'],
      homeAvoidHi: ['गहरा लाल', 'गेरुआ नारंगी'],
      homeGuideEn: 'Nurtures restful aesthetics, sensual comfort, and family prosperity.',
      homeGuideHi: 'गृह सौंदर्य, दांपत्य मधुरता व गहरी विश्रामदायी नींद प्रदान करता है।',

      officeRecEn: ['Off-White', 'Warm Beige', 'Jade Green', 'Walnut Brown'],
      officeRecHi: ['ऑफ-व्हाइट', 'वार्म बेज', 'जेड ग्रीन', 'अखरोट भूरा'],
      officeAvoidEn: ['Bright Red Accents'],
      officeAvoidHi: ['भड़कीले लाल पर्दे/दीवारें'],
      officeGuideEn: 'Builds enduring business partnerships and grounded commercial stability.',
      officeGuideHi: 'व्यापारिक स्थिरता, स्थायी ग्राहक व धैर्यवान निर्णय क्षमता देता है।',

      brandRecEn: ['Rose Gold', 'Silver', 'Teal Green', 'Royal Navy'],
      brandRecHi: ['रोज़ गोल्ड', 'सिल्वर', 'टील ग्रीन', 'रॉयल नेवी'],
      brandAvoidEn: ['Harsh Orange-Red'],
      brandAvoidHi: ['कर्कश लाल-नारंगी'],
      brandGuideEn: 'Communicates quality, premium durability, and aesthetic perfection.',
      brandGuideHi: 'ब्रांड में उच्च गुणवत्ता, विश्वसनीयता और कलात्मक गरिमा दर्शाता है।',

      gadgetRecEn: ['Starlight White', 'Silver', 'Pastel Green Case'],
      gadgetRecHi: ['स्टारलाइट सफेद', 'सिल्वर', 'हल्का हरा कवर'],
      gadgetAvoidEn: ['Red Rugged Cases'],
      gadgetAvoidHi: ['उग्र लाल रग्ड केस'],
      gadgetGuideEn: 'Soothes visual strain and enhances digital creative workflow.',
      gadgetGuideHi: 'डिजिटल कार्य में रचनात्मकता व आंखों को शीतलता प्रदान करता है।',

      sigRecEn: ['Dark Blue Ink', 'Emerald Green Ink'],
      sigRecHi: ['गहरी नीली स्याही', 'पन्ना हरी स्याही'],
      sigAvoidEn: ['Red Ink'],
      sigAvoidHi: ['लाल स्याही'],
      sigGuideEn: 'Brings legal permanence and financial backing to signed commitments.',
      sigGuideHi: 'हस्ताक्षरित समझौतों में कानूनी स्थायित्व और आर्थिक लाभ दिलाता है।'
    },
    Gemini: {
      vehicleRecEn: ['Light Emerald Green', 'Silver', 'White', 'Light Blue'],
      vehicleRecHi: ['हल्का हरा', 'चांदी', 'सफेद', 'आसमानी नीला'],
      vehicleAvoidEn: ['Deep Maroon', 'Blood Red'],
      vehicleAvoidHi: ['गहरा मैरून', 'खूनी लाल'],
      vehicleGuideEn: '4th house Virgo (Mercury). Green and Silver minimize mechanical electronics issues and boost transit focus.',
      vehicleGuideHi: 'चतुर्थ भाव कन्या (बुध)। हरा व सिल्वर रंग यात्रा में मानसिक एकाग्रता व वाहन की सेंसर्स/इलेक्ट्रॉनिक्स सुरक्षा देता है।',
      vehicleSecretEn: 'Place a small green adventurine crystal or tulsi leaf emblem on the console.',
      vehicleSecretHi: 'गाड़ी में तुलसी दल का प्रतीक या हरा क्रिस्टल रखने से यात्रा निर्विघ्न रहती है।',

      walletRecEn: ['Forest Green', 'Jade', 'Navy Blue'],
      walletRecHi: ['गहरा हरा', 'जेड', 'नेवी ब्लू'],
      walletAvoidEn: ['Crimson Red', 'Bright Scarlet'],
      walletAvoidHi: ['सिंदूरी लाल', 'उग्र लाल'],
      walletGuideEn: 'Activates Mercury trading intellect and multiples multiple income streams.',
      walletGuideHi: 'व्यापारिक बुद्धि व बहु-आय स्रोतों को गतिशील रखता है।',

      homeRecEn: ['Mint Green', 'Light Cream', 'Sky Blue', 'Pastel Yellow'],
      homeRecHi: ['मिंट ग्रीन', 'हल्का क्रीम', 'आसमानी', 'हल्का पीला'],
      homeAvoidEn: ['Heavy Maroon', 'Dark Brown'],
      homeAvoidHi: ['भारी मैरून', 'गहरा भूरा'],
      homeGuideEn: 'Encourages cheerful dialogue, reading clarity, and stress-free recreation.',
      homeGuideHi: 'संवाद में मधुरता, अध्ययन में रुचि व पारिवारिक प्रसन्नता बढ़ाता है।',

      officeRecEn: ['Sage Green', 'Birch White', 'Light Grey', 'Turquoise'],
      officeRecHi: ['सेज ग्रीन', 'सफेद', 'हल्का स्लेटी', 'फिरोजा'],
      officeAvoidEn: ['Blood Red'],
      officeAvoidHi: ['गहरा लाल'],
      officeGuideEn: 'Sharpens marketing brilliance, media communication, and rapid multitasking.',
      officeGuideHi: 'मार्केटिंग, मीडिया संवाद और त्वरित गणनाओं में सफलता देता है।',

      brandRecEn: ['Vibrant Green', 'Bright Cyan', 'Yellow Accent'],
      brandRecHi: ['चमकीला हरा', 'सियान नीला', 'पीला टच'],
      brandAvoidEn: ['Dull Brown'],
      brandAvoidHi: ['धूमिल भूरा'],
      brandGuideEn: 'Exudes youthfulness, intelligence, agility, and modern connectivity.',
      brandGuideHi: 'ब्रांड को आधुनिक, युवा, सुगम और विचारशील बनाता है।',

      gadgetRecEn: ['Mint Green', 'Silver Aluminum', 'Sky Blue Case'],
      gadgetRecHi: ['मिंट ग्रीन', 'सिल्वर एल्युमिनियम', 'आसमानी कवर'],
      gadgetAvoidEn: ['Maroon Leather'],
      gadgetAvoidHi: ['मैरून लेदर कवर'],
      gadgetGuideEn: 'Promotes rapid learning, writing efficiency, and speech clarity on calls.',
      gadgetGuideHi: 'कॉलिंग, लेखन व शोध में स्पष्टता और त्वरित गति प्रदान करता है।',

      sigRecEn: ['Bright Blue Ink', 'Green Gel Ink'],
      sigRecHi: ['चमकीली नीली स्याही', 'हरी जेल स्याही'],
      sigAvoidEn: ['Black Ballpoint with faded ink'],
      sigAvoidHi: ['फीकी काली स्याही'],
      sigGuideEn: 'Fosters fast client closures and intellectual copyright sanctity.',
      sigGuideHi: 'व्यापारिक सौदों को तुरंत अंतिम रूप देने में सहायक।।'
    },
    Cancer: {
      vehicleRecEn: ['Pearl White', 'Moon Silver', 'Ivory', 'Soft Sea Green'],
      vehicleRecHi: ['मोती जैसा सफेद', 'सिल्वर', 'आइवरी', 'हल्का समुद्री हरा'],
      vehicleAvoidEn: ['Jet Black', 'Dark Navy Blue', 'Charcoal'],
      vehicleAvoidHi: ['गहरा काला', 'गहरा नेवी ब्लू', 'कोयला स्लेटी'],
      vehicleGuideEn: '4th house Libra (Venus). White and Moon Silver protect domestic safety and eliminate nervous driving.',
      vehicleGuideHi: 'चतुर्थ भाव तुला (शुक्र)। सफेद व सिल्वर रंग यात्रा को शांत, सुखद और पारिवारिक सुरक्षा से परिपूर्ण बनाते हैं।',
      vehicleSecretEn: 'Keep a small silver coin dipped in Ganga Jal or sandalwood pouch in the car.',
      vehicleSecretHi: 'गाड़ी में चांदी का छोटा सिक्का या चंदन का पाउच रखने से मानसिक शांति रहती है।',

      walletRecEn: ['Pure White / Pearl', 'Soft Cream', 'Silver Accents'],
      walletRecHi: ['सफेद/मोती', 'हल्का क्रीम', 'चांदी बॉर्डर'],
      walletAvoidEn: ['Black Leather', 'Charcoal'],
      walletAvoidHi: ['काला लेदर', 'कोयला स्लेटी'],
      walletGuideEn: 'Protects emotional wealth and guards against impulsive expenditure in stress.',
      walletGuideHi: 'तनाव में अनावश्यक खर्चों से रक्षा और धन में निरंतर वृद्धि कराता है।',

      homeRecEn: ['Seafoam White', 'Moon Cream', 'Pale Blue', 'Pearly Sand'],
      homeRecHi: ['सफेद', 'मिल्की क्रीम', 'हल्का नीला', 'चांदी जैसा रंग'],
      homeAvoidEn: ['Jet Black Walls', 'Dark Heavy Grey'],
      homeAvoidHi: ['काली दीवारें', 'गहरा भारी स्लेटी'],
      homeGuideEn: 'The ultimate sanctuary for deep emotional rejuvenation and sound sleep.',
      homeGuideHi: 'गृह सुख, मानसिक शांति व पारिवारिक अपनत्व का श्रेष्ठ वातावरण बनाता है।',

      officeRecEn: ['Pearl White', 'Soft Sage', 'Light Timber'],
      officeRecHi: ['मोती सफेद', 'हल्का हरा', 'हल्की लकड़ी का रंग'],
      officeAvoidEn: ['Pitch Black Desks'],
      officeAvoidHi: ['बिल्कुल काली मेज/कुर्सी'],
      officeGuideEn: 'Inspires deep trust in clients, patients, and strategic partners.',
      officeGuideHi: 'ग्राहकों व साझीदारों में गहरा विश्वास और सौम्य सम्मान स्थापित करता है।',

      brandRecEn: ['Silver', 'Pure White', 'Ocean Blue', 'Emerald Green'],
      brandRecHi: ['सिल्वर', 'शुद्ध सफेद', 'समुद्री नीला', 'पन्ना हरा'],
      brandAvoidEn: ['Dull Black / Grey'],
      brandAvoidHi: ['धूमिल काला/ग्रे'],
      brandGuideEn: 'Signals empathy, absolute integrity, care, and dependable service.',
      brandGuideHi: 'विश्वसनीयता, सेवा भाव और अटूट ईमानदारी का संदेश देता है।',

      gadgetRecEn: ['White Finish', 'Silver Case', 'Clear Transparent'],
      gadgetRecHi: ['सफेद फोन', 'सिल्वर केस', 'पारदर्शी कवर'],
      gadgetAvoidEn: ['Matte Black Cases'],
      gadgetAvoidHi: ['मैट ब्लैक कवर'],
      gadgetGuideEn: 'Keeps thoughts emotionally uncluttered and grounded.',
      gadgetGuideHi: 'विचारों में स्पष्टता और मानसिक शांति बनाए रखता है।',

      sigRecEn: ['Royal Blue Ink', 'Silver/Blue Accent'],
      sigRecHi: ['रॉयल ब्लू स्याही', 'नीली स्याही'],
      sigAvoidEn: ['Black Ink for personal bonds'],
      sigAvoidHi: ['काली स्याही'],
      sigGuideEn: 'Ensures long-term goodwill, trust, and peaceful resolutions.',
      sigGuideHi: 'दीर्घकालिक साख, भरोसा व संबंधों में स्थायित्व देता है।'
    },
    Leo: {
      vehicleRecEn: ['Gold / Champagne', 'Ruby Bronze', 'Sun Orange', 'Radiant White'],
      vehicleRecHi: ['सुनहरा / शैंपेन', 'तांबई लाल', 'सूर्य नारंगी', 'उज्ज्वल सफेद'],
      vehicleAvoidEn: ['Dull Black', 'Dark Navy Blue', 'Charcoal Grey'],
      vehicleAvoidHi: ['धूमिल काला', 'गहरा नेवी ब्लू', 'कोयला स्लेटी'],
      vehicleGuideEn: '4th house Scorpio (Mars). Warm Bronze, Champagne or Pure White reflects Sun-Mars majesty and prevents sudden breakdowns.',
      vehicleGuideHi: 'चतुर्थ भाव वृश्चिक (मंगल)। शैंपेन, सफेद या तांबई रंग वाहन की सुरक्षा, गति व प्रतिष्ठा में वृद्धि करता है।',
      vehicleSecretEn: 'Keep a copper coin or Gayatri Mantra card in the glove compartment.',
      vehicleSecretHi: 'ग्लव बॉक्स में तांबे का सिक्का या गायत्री मंत्र पत्रक रखने से दुर्घटना योग टलते हैं।',

      walletRecEn: ['Rich Tan Leather', 'Golden Brown', 'Copper Red'],
      walletRecHi: ['रिच टैन लेदर', 'गोल्डन ब्राउन', 'तांबई लाल'],
      walletAvoidEn: ['Black', 'Dull Slate'],
      walletAvoidHi: ['काला', 'स्लेटी'],
      walletGuideEn: 'Commands authoritative cash flow and investment dominion.',
      walletGuideHi: 'आर्थिक अधिकार, बड़े निवेश लाभ व मान-सम्मान को बढ़ाता है।',

      homeRecEn: ['Warm Amber', 'Golden Cream', 'Ivory', 'Soft Terracotta'],
      homeRecHi: ['अंबर', 'गोल्डन क्रीम', 'आइवरी', 'टेराकोटा'],
      homeAvoidEn: ['Dark Cold Grey', 'Black Accents'],
      homeAvoidHi: ['ठंडा स्लेटी', 'काले शेड्स'],
      homeGuideEn: 'Fills the living space with warmth, vitality, and stately dignity.',
      homeGuideHi: 'घर में सूर्य जैसा तेज, ऊर्जा और पारिवारिक गरिमा का संचार करता है।',

      officeRecEn: ['Mahogany', 'Rich Gold Accent', 'Warm Ivory', 'Crimson Highlights'],
      officeRecHi: ['महोगनी वुड', 'गोल्ड टच', 'वार्म आइवरी', 'लाल टच'],
      officeAvoidEn: ['Dull Blue-Grey'],
      officeAvoidHi: ['धूमिल नीला-स्लेटी'],
      officeGuideEn: 'Commands natural authority, team allegiance, and executive victory.',
      officeGuideHi: 'नेतृत्व क्षमता, टीम में अनुशासन और व्यावसायिक विजय दिलाता है।',

      brandRecEn: ['Gold', 'Crimson', 'Regal Orange', 'Imperial Purple'],
      brandRecHi: ['स्वर्ण (Gold)', 'लाल', 'शाही नारंगी', 'बैंगनी'],
      brandAvoidEn: ['Cold Grey / Black combo'],
      brandAvoidHi: ['ठंडा ग्रे/काला'],
      brandGuideEn: 'Instills premium luxury, unmatched royalty, and commanding market authority.',
      brandGuideHi: 'मार्केट में सर्वोच्च स्थान, विलासिता और नेतृत्वकारी पहचान देता है।',

      gadgetRecEn: ['Gold / Desert Titanium', 'Warm Amber Case'],
      gadgetRecHi: ['गोल्ड / टाइटेनियम', 'अंबर / भूरा कवर'],
      gadgetAvoidEn: ['Black Matte Cover'],
      gadgetAvoidHi: ['काला कवर'],
      gadgetGuideEn: 'Elevates presence in executive communications and video meetings.',
      gadgetGuideHi: 'व्यापारिक वार्ताओं व ऑनलाइन बैठकों में प्रभावशाली आभा देता है।',

      sigRecEn: ['Royal Blue Fountain Pen', 'Purple Ink'],
      sigRecHi: ['रॉयल ब्लू फाउंटेन पेन', 'बैंगनी स्याही'],
      sigAvoidEn: ['Scratchy Black Ink'],
      sigAvoidHi: ['रफ काली स्याही'],
      sigGuideEn: 'Ensures royal executive validity and undisputed leadership approval.',
      sigGuideHi: 'हस्ताक्षर में प्रामाणिकता, वजन और शासकीय स्वीकृति दिलाता है।'
    },
    Virgo: {
      vehicleRecEn: ['Emerald Green', 'Forest Green', 'Silver', 'Pearl White'],
      vehicleRecHi: ['पन्ना हरा', 'गहरा हरा', 'चांदी', 'मोती सफेद'],
      vehicleAvoidEn: ['Bright Blood Red', 'Neon Orange'],
      vehicleAvoidHi: ['उग्र लाल', 'भड़कीला नारंगी'],
      vehicleGuideEn: '4th house Sagittarius (Jupiter). Green, White and Silver ensure mechanical durability and fuel efficiency.',
      vehicleGuideHi: 'चतुर्थ भाव धनु (बृहस्पति)। हरा, सफेद व सिल्वर रंग वाहन के रखरखाव खर्च को घटाता है व यात्रा सुरक्षित रखता है।',
      vehicleSecretEn: 'Store a small brass yantra or yellow thread in the driver sun visor.',
      vehicleSecretHi: 'ड्राइवर सन-वाइज़र में पीला धागा या छोटा पीतल का यंत्र रखने से सुरक्षा रहती है।',

      walletRecEn: ['Forest Green', 'Dark Olive', 'Chocolate Brown'],
      walletRecHi: ['गहरा हरा', 'ओलिव ग्रीन', 'चॉकलेट भूरा'],
      walletAvoidEn: ['Fiery Red'],
      walletAvoidHi: ['भड़कीला लाल'],
      walletGuideEn: 'Calculates every rupee precisely, preventing financial leakages.',
      walletGuideHi: 'वित्तीय अनुशासन बनाए रखता है और व्यर्थ खर्चों पर रोक लगाता है।',

      homeRecEn: ['Soft Sage Green', 'Crisp White', 'Pale Beige', 'Light Khaki'],
      homeRecHi: ['हल्का हरा (Sage)', 'उज्ज्वल सफेद', 'बेज', 'हल्का खाकी'],
      homeAvoidEn: ['Aggressive Red Walls'],
      homeAvoidHi: ['उग्र लाल दीवारें'],
      homeGuideEn: 'Soothes nervous tension, purifies environment, and promotes health recovery.',
      homeGuideHi: 'मानसिक तनाव शांत करता है और स्वास्थ्य व स्वच्छता में सुधार लाता है।',

      officeRecEn: ['Jade Green', 'White Oak', 'Cool Grey', 'Pale Amber'],
      officeRecHi: ['जेड हरा', 'व्हाइट ओक', 'हल्का स्लेटी', 'हल्का अंबर'],
      officeAvoidEn: ['Blood Red'],
      officeAvoidHi: ['खूनी लाल'],
      officeGuideEn: 'Enhances micro-detail accuracy, research depth, and audit perfection.',
      officeGuideHi: 'लेखा परीक्षा, कोडिंग, डेटा विश्लेषण व सटीकता में उत्कृष्टता देता है।',

      brandRecEn: ['Emerald Green', 'Deep Teal', 'Clean White', 'Silver'],
      brandRecHi: ['पन्ना हरा', 'गहरा टील', 'सफेद', 'सिल्वर'],
      brandAvoidEn: ['Neon Pink / Red'],
      brandAvoidHi: ['नियॉन पिंक/लाल'],
      brandGuideEn: 'Communicates medical hygiene, scientific precision, and flawless service.',
      brandGuideHi: 'वैज्ञानिक सटीकता, स्वच्छता और दोषरहित सेवा का विश्वास जगाता है।',

      gadgetRecEn: ['Green Tone', 'Natural Titanium', 'Clear Protective Case'],
      gadgetRecHi: ['हरा शेड', 'नेचुरल टाइटेनियम', 'पारदर्शी केस'],
      gadgetAvoidEn: ['Crimson Red Cover'],
      gadgetAvoidHi: ['सिंदूरी लाल कवर'],
      gadgetGuideEn: 'Guards against eye fatigue and organized data management.',
      gadgetGuideHi: 'आंखों की थकान घटाता है और व्यवस्थित नोट्स बनाने में सहायक।',

      sigRecEn: ['Green Gel / Rollerball Ink', 'Dark Blue Ink'],
      sigRecHi: ['हरी जेल स्याही', 'गहरी नीली स्याही'],
      sigAvoidEn: ['Red Ink for signatures'],
      sigAvoidHi: ['लाल स्याही'],
      sigGuideEn: 'The hallmark of verified accuracy, audit clearance, and commercial safety.',
      sigGuideHi: 'दस्तावेजों की प्रामाणिकता और आर्थिक सुरक्षा का प्रतीक।'
    },
    Libra: {
      vehicleRecEn: ['Diamond White', 'Silver Blue', 'Turquoise', 'Soft Metallic Blue'],
      vehicleRecHi: ['सफेद', 'सिल्वर नीला', 'फिरोजा', 'मेटैलिक नीला'],
      vehicleAvoidEn: ['Dark Mustard Yellow', 'Muddy Rust Brown'],
      vehicleAvoidHi: ['गहरा सरसों पीला', 'मटमैला भूरा'],
      vehicleGuideEn: '4th house Capricorn (Saturn). Diamond White or Silver Blue pairs Venusian elegance with Saturnian endurance.',
      vehicleGuideHi: 'चतुर्थ भाव मकर (शनि)। सफेद व सिल्वर-ब्लू रंग वाहन को सुरुचिपूर्ण और दीर्घायु बनाता है।',
      vehicleSecretEn: 'Keep a small quartz crystal or lavender fragrance sachet inside.',
      vehicleSecretHi: 'कार में स्फटिक या लैवेंडर इत्र का स्पर्श रखने से सकारात्मक ऊर्जा बनी रहती है।',

      walletRecEn: ['Silver / Metallic', 'Pastel Pink', 'Sky Blue', 'White'],
      walletRecHi: ['सिल्वर', 'गुलाबी', 'आसमानी', 'सफेद'],
      walletAvoidEn: ['Dark Mustard Yellow'],
      walletAvoidHi: ['सरसों पीला'],
      walletGuideEn: 'Brings aesthetic equilibrium, luxury acquisitions, and partnership profits.',
      walletGuideHi: 'साझेदारी लाभ, विलासिता सुख और आर्थिक संतुलन बनाए रखता है।',

      homeRecEn: ['Rose Ivory', 'Sky Blue Pastel', 'Chalk White', 'Lilac'],
      homeRecHi: ['गुलाबी आइवरी', 'हल्का नीला', 'सफेद', 'हल्का जामुनी'],
      homeAvoidEn: ['Dark Ochre Yellow'],
      homeAvoidHi: ['गहरा गेरुआ पीला'],
      homeGuideEn: 'Fosters romantic harmony, social grace, and sophisticated hospitality.',
      homeGuideHi: 'दांपत्य प्रेम, सामाजिक सम्मान व कलात्मक घर का वातावरण तैयार करता है।',

      officeRecEn: ['Dove Grey', 'Pure White', 'Turquoise Accents', 'Glass & Chrome'],
      officeRecHi: ['हल्का स्लेटी', 'शुद्ध सफेद', 'फिरोजा टच', 'ग्लास व क्रोम'],
      officeAvoidEn: ['Mustard Yellow'],
      officeAvoidHi: ['गहरा पीला'],
      officeGuideEn: 'Attracts affluent clientele, diplomatic breakthroughs, and legal mediation wins.',
      officeGuideHi: 'उच्च स्तरीय ग्राहक, कूटनीतिक सफलता और मध्यस्थता में विजय दिलाता है।',

      brandRecEn: ['Rose Gold', 'Lavender', 'Silver', 'Powder Blue'],
      brandRecHi: ['रोज़ गोल्ड', 'लैवेंडर', 'सिल्वर', 'पाउडर ब्लू'],
      brandAvoidEn: ['Harsh Ochre'],
      brandAvoidHi: ['कर्कश पीला'],
      brandGuideEn: 'The signature of high fashion, balance, justice, and refined luxury.',
      brandGuideHi: 'फैशन, सौंदर्य, न्याय और सुरुचिपूर्ण विलासिता का प्रतीक।',

      gadgetRecEn: ['Silver / White', 'Blue Pastel Case', 'Rose Gold Accents'],
      gadgetRecHi: ['सिल्वर / व्हाइट', 'हल्का नीला कवर', 'रोज़ गोल्ड'],
      gadgetAvoidEn: ['Mustard Yellow Cover'],
      gadgetAvoidHi: ['सरसों पीला कवर'],
      gadgetGuideEn: 'Harmonizes relationship texts and creative screen engagements.',
      gadgetGuideHi: 'सकारात्मक संवाद और रचनात्मक कार्यों में संतुलन रखता है।',

      sigRecEn: ['Royal Blue Fountain Ink', 'Turquoise/Blue Ink'],
      sigRecHi: ['रॉयल ब्लू स्याही', 'आसमानी नीली स्याही'],
      sigAvoidEn: ['Yellow or Orange Ink'],
      sigAvoidHi: ['पीली या नारंगी स्याही'],
      sigGuideEn: 'Ensures fairness, balanced dispute settlement, and mutual profitability.',
      sigGuideHi: 'निष्पक्षता, विवादों का शांतिपूर्ण समाधान और पारस्परिक लाभ देता है।'
    },
    Scorpio: {
      vehicleRecEn: ['Coral Red', 'Deep Maroon', 'Warm Copper', 'Pearl White'],
      vehicleRecHi: ['मूंगा लाल', 'गहरा मैरून', 'तांबई रंग', 'मोती सफेद'],
      vehicleAvoidEn: ['Pitch Black', 'Dark Blue', 'Bottle Green'],
      vehicleAvoidHi: ['गहरा काला', 'गहरा नीला', 'गहरा हरा'],
      vehicleGuideEn: '4th house Aquarius (Saturn). Warm Copper or Coral Red shields against sudden road hazards and road aggression.',
      vehicleGuideHi: 'चतुर्थ भाव कुंभ (शनि)। तांबई, मूंगा लाल या सफेद रंग वाहन की सुरक्षा व त्वरित सुरक्षा तंत्र को मजबूत रखता है।',
      vehicleSecretEn: 'Tie a red mauli (sacred thread) on the steering column.',
      vehicleSecretHi: 'स्टीयरिंग कॉलम पर लाल कलावा (मौली) बांधने से सुरक्षा कवच बनता है।',

      walletRecEn: ['Deep Mahogany', 'Rust Red', 'Cognac Tan'],
      walletRecHi: ['महोगनी', 'मूंगा लाल', 'तांबई भूरा'],
      walletAvoidEn: ['Jet Black', 'Dark Navy'],
      walletAvoidHi: ['काला', 'गहरा नेवी'],
      walletGuideEn: 'Protects secret emergency funds and compounds wealth through transformation.',
      walletGuideHi: 'आपातकालीन संचय की सुरक्षा और गुप्त धन में वृद्धि कराता है।',

      homeRecEn: ['Warm Terracotta', 'Alabaster White', 'Deep Rose', 'Warm Sand'],
      homeRecHi: ['टेराकोटा', 'सफेद', 'गुलाबी लाल', 'वार्म सैंड'],
      homeAvoidEn: ['Pitch Black Accent Walls', 'Cold Dark Grey'],
      homeAvoidHi: ['काली दीवारें', 'ठंडा गहरा स्लेटी'],
      homeGuideEn: 'Provides an impregnable sanctuary for deep psychological peace and meditation.',
      homeGuideHi: 'गहरी मानसिक शांति, साधना व पारिवारिक एकांत का श्रेष्ठ वातावरण देता है।',

      officeRecEn: ['Rich Walnut Wood', 'Burgundy Leather', 'Warm Cream Walls'],
      officeRecHi: ['अखरोट लकड़ी', 'बरगंडी लेदर', 'क्रीम दीवारें'],
      officeAvoidEn: ['All-Black Modern Desks'],
      officeAvoidHi: ['पूरी तरह काली मेज'],
      officeGuideEn: 'Strengthens crisis turnaround leadership, investigative depth, and discretion.',
      officeGuideHi: 'संकट प्रबंधन, गूढ़ अनुसंधान और व्यावसायिक गोपनीयता में शक्ति देता है।',

      brandRecEn: ['Deep Crimson', 'Rust Gold', 'Copper', 'Midnight Blue with Red Accent'],
      brandRecHi: ['गहरा लाल', 'तांबई गोल्ड', 'कॉपर', 'लाल-ब्लू कॉम्बो'],
      brandAvoidEn: ['Dull Black only'],
      brandAvoidHi: ['केवल काला'],
      brandGuideEn: 'Commands deep magnetic trust, transformative power, and strategic mystery.',
      brandGuideHi: 'गहरा आकर्षण, परिवर्तनकारी शक्ति और रणनीतिक प्रभाव स्थापित करता है।',

      gadgetRecEn: ['Product Red / Copper', 'Dark Brown Leather Case'],
      gadgetRecHi: ['रेड / कॉपर', 'गहरा भूरा लेदर कवर'],
      gadgetAvoidEn: ['Pure Black Case'],
      gadgetAvoidHi: ['काला कवर'],
      gadgetGuideEn: 'Guards cybersecurity intuition and protects confidential conversations.',
      gadgetGuideHi: 'गोपनीय वार्ताओं की रक्षा और साइबर सुरक्षा अंतर्ज्ञान बढ़ाता है।',

      sigRecEn: ['Dark Blue Ink', 'Crimson Red (for approvals/marks only)'],
      sigRecHi: ['गहरी नीली स्याही', 'गहरी लाल स्याही (केवल सत्यापन हेतु)'],
      sigAvoidEn: ['Faded Grey Ink'],
      sigAvoidHi: ['फीकी स्लेटी स्याही'],
      sigGuideEn: 'Solidifies definitive willpower and irreversible contractual breakthroughs.',
      sigGuideHi: 'दृढ़ इच्छाशक्ति और निर्णायक समझौतों को अटूट बनाता है।'
    },
    Sagittarius: {
      vehicleRecEn: ['Golden Yellow', 'Warm Amber', 'Silver White', 'Saffron'],
      vehicleRecHi: ['सुनहरा पीला', 'अंबर', 'सिल्वर सफेद', 'केसरिया'],
      vehicleAvoidEn: ['Jet Black', 'Dark Navy Blue', 'Charcoal'],
      vehicleAvoidHi: ['गहरा काला', 'गहरा नेवी ब्लू', 'कोयला स्लेटी'],
      vehicleGuideEn: '4th house Pisces (Jupiter). Amber, Yellow or White aligns with long-distance travel, pilgrimage safety, and joy.',
      vehicleGuideHi: 'चतुर्थ भाव मीन (गुरु)। अंबर, पीला या सफेद रंग लंबी यात्राओं, तीर्थाटन व पारिवारिक सुरक्षा में अत्यंत शुभ है।',
      vehicleSecretEn: 'Keep a yellow handkerchief or small piece of turmeric in the dash.',
      vehicleSecretHi: 'डैशबोर्ड में पीला रुमाल या हल्दी की गांठ रखने से देवगुरु की कृपा बनी रहती है।',

      walletRecEn: ['Golden Yellow', 'Mustard Tan', 'Warm Brown'],
      walletRecHi: ['सुनहरा पीला', 'सरसों टैन', 'गर्म भूरा'],
      walletAvoidEn: ['Jet Black'],
      walletAvoidHi: ['काला'],
      walletGuideEn: 'Multiplies dharma-based prosperity, consulting fees, and educational wealth.',
      walletGuideHi: 'परामर्श, शिक्षा और धर्मयुक्त धन संचय में लगातार वृद्धि कराता है।',

      homeRecEn: ['Sunshine Yellow', 'Warm Cream', 'Ivory', 'Soft Saffron'],
      homeRecHi: ['हल्का पीला', 'वार्म क्रीम', 'आइवरी', 'केसरिया'],
      homeAvoidEn: ['Dark Slate / Charcoal'],
      homeAvoidHi: ['गहरा स्लेटी / काला'],
      homeGuideEn: 'Fills the home with wisdom, spiritual grace, joyous laughter, and optimism.',
      homeGuideHi: 'घर में ज्ञान, ईश्वरीय कृपा, उत्साह और सकारात्मक ऊर्जा भरता है।',

      officeRecEn: ['Oak Timber', 'Warm Saffron Accents', 'Cream Walls', 'Brass Details'],
      officeRecHi: ['ओक वुड', 'केसरिया टच', 'क्रीम दीवारें', 'पीतल एक्सेंट'],
      officeAvoidEn: ['Dark Cold Navy'],
      officeAvoidHi: ['ठंडा गहरा नेवी'],
      officeGuideEn: 'Expands mentorship clout, advisory leadership, and global business growth.',
      officeGuideHi: 'सलाहकार की भूमिका, वैश्विक व्यापार और मार्गदर्शन में ख्याति दिलाता है।',

      brandRecEn: ['Golden Yellow', 'Royal Blue with Gold', 'Saffron', 'Amber'],
      brandRecHi: ['सुनहरा पीला', 'नीला व गोल्ड', 'केसरिया', 'अंबर'],
      brandAvoidEn: ['Dull Black / Grey'],
      brandAvoidHi: ['धूमिल काला/ग्रे'],
      brandGuideEn: 'Represents global expansion, higher learning, ethical leadership, and truth.',
      brandGuideHi: 'विश्वसनीय ज्ञान, वैश्विक विस्तार, नैतिक नेतृत्व और सत्य का प्रतीक।',

      gadgetRecEn: ['Gold / Starlight Finish', 'Yellow or Tan Leather Case'],
      gadgetRecHi: ['गोल्ड / स्टारलाइट', 'पीला या टैन लेदर कवर'],
      gadgetAvoidEn: ['Black Matte Case'],
      gadgetAvoidHi: ['मैट ब्लैक कवर'],
      gadgetGuideEn: 'Inspires broad conceptual vision and visionary content creation.',
      gadgetGuideHi: 'दूरदर्शी सोच और ज्ञानवर्धक अध्ययन में एकाग्रता देता है।',

      sigRecEn: ['Royal Blue Fountain Pen', 'Purple Ink'],
      sigRecHi: ['रॉयल ब्लू फाउंटेन पेन', 'बैंगनी स्याही'],
      sigAvoidEn: ['Black Ballpoint'],
      sigAvoidHi: ['काली बॉल पेन'],
      sigGuideEn: 'Invokes divine sanction, legal righteousness, and scholarly authority.',
      sigGuideHi: 'हस्ताक्षर में धार्मिक शुद्धता, न्याय और बौद्धिक प्रतिष्ठा भरता है।'
    },
    Capricorn: {
      vehicleRecEn: ['Navy Blue', 'Steel Grey', 'Charcoal', 'Metallic Blue', 'White'],
      vehicleRecHi: ['नेवी ब्लू', 'स्टील स्लेटी', 'गहरा स्लेटी', 'मेटैलिक नीला', 'सफेद'],
      vehicleAvoidEn: ['Bright Scarlet Red', 'Fiery Orange'],
      vehicleAvoidHi: ['भड़कीला लाल', 'उग्र नारंगी'],
      vehicleGuideEn: '4th house Aries (Mars). Navy Blue, Steel Grey or White keeps engine heat controlled and maintains long vehicle life.',
      vehicleGuideHi: 'चतुर्थ भाव मेष (मंगल)। नेवी ब्लू, स्टील ग्रे या सफेद रंग वाहन की मजबूती व दीर्घकालिक उपयोगिता बनाए रखता है।',
      vehicleSecretEn: 'Keep a small iron nail or blue sapphire essence pouch under the seat.',
      vehicleSecretHi: 'सीट के नीचे छोटा लोहे का छल्ला या नीले कपड़े का टुकड़ा रखने से शनि की स्थिरता मिलती है।',

      walletRecEn: ['Classic Black Leather', 'Dark Navy', 'Steel Grey'],
      walletRecHi: ['क्लासिक काला लेदर', 'गहरा नेवी', 'स्टील स्लेटी'],
      walletAvoidEn: ['Bright Red', 'Neon Orange'],
      walletAvoidHi: ['भड़कीला लाल', 'नारंगी'],
      walletGuideEn: 'Builds patient, compounding capital and immovable asset portfolios.',
      walletGuideHi: 'दीर्घकालिक पूंजी निर्माण और अचल संपत्ति संचय में मददगार।',

      homeRecEn: ['Dove Grey', 'Stone White', 'Soft Beige', 'Forest Green Accents'],
      homeRecHi: ['स्लेटी', 'सफेद पत्थर रंग', 'बेज', 'हल्का वन हरा'],
      homeAvoidEn: ['Bright Red Walls', 'Hot Orange'],
      homeAvoidHi: ['उग्र लाल दीवारें', 'तीखा नारंगी'],
      homeGuideEn: 'Provides quiet, orderly discipline, restorative focus, and domestic stability.',
      homeGuideHi: 'घर में शांति, सुव्यवस्था, अनुशासन और मानसिक स्थिरता लाता है।',

      officeRecEn: ['Dark Slate Desks', 'Steel Chrome', 'Navy Blue Chairs', 'Charcoal'],
      officeRecHi: ['गहरी स्लेटी मेज', 'स्टील क्रोम', 'नेवी कुर्सियां', 'कोयला रंग'],
      officeAvoidEn: ['Hot Red Decor'],
      officeAvoidHi: ['लाल रंग की सजावट'],
      officeGuideEn: 'Establishes formidable corporate authority, perseverance, and executive control.',
      officeGuideHi: 'कॉर्पोरेट जगत में दबदबा, धैर्य, नियमबद्धता और दृढ़ नियंत्रण स्थापित करता है।',

      brandRecEn: ['Navy Blue', 'Steel Grey', 'Forest Green', 'Monochrome Black-White'],
      brandRecHi: ['नेवी ब्लू', 'स्टील ग्रे', 'गहरा हरा', 'ब्लैक-व्हाइट'],
      brandAvoidEn: ['Neon Pink / Orange'],
      brandAvoidHi: ['नियॉन पिंक/नारंगी'],
      brandGuideEn: 'Communicates industrial endurance, institutional trust, and ironclad stability.',
      brandGuideHi: 'संस्थागत विश्वास, अटूट विश्वसनीयता और दीर्घकालिक स्थायित्व दर्शाता है।',

      gadgetRecEn: ['Space Black / Space Grey', 'Dark Navy Blue Case'],
      gadgetRecHi: ['स्पेस ब्लैक / ग्रे', 'नेवी ब्लू कवर'],
      gadgetAvoidEn: ['Bright Red Case'],
      gadgetAvoidHi: ['भड़कीला लाल कवर'],
      gadgetGuideEn: 'Supports disciplined deep-work marathons and organized executive tracking.',
      gadgetGuideHi: 'गहन कार्य (Deep Work) और व्यावसायिक प्रबंधन में एकाग्रता बनाए रखता है।',

      sigRecEn: ['Black Ink', 'Dark Blue Ink'],
      sigRecHi: ['काली स्याही', 'गहरी नीली स्याही'],
      sigAvoidEn: ['Red Ink'],
      sigAvoidHi: ['लाल स्याही'],
      sigGuideEn: 'Signs iron-clad legal covenants that withstand scrutiny and passage of time.',
      sigGuideHi: 'कानूनी रूप से अभेद्य और समय की कसौटी पर खरे उतरने वाले हस्ताक्षर।'
    },
    Aquarius: {
      vehicleRecEn: ['Electric Blue', 'Ultramarine', 'Deep Violet', 'Silver', 'White'],
      vehicleRecHi: ['चमकीला नीला', 'गहरा नीला', 'बैंगनी', 'सिल्वर', 'सफेद'],
      vehicleAvoidEn: ['Blood Red', 'Muddy Brown'],
      vehicleAvoidHi: ['खूनी लाल', 'मटमैला भूरा'],
      vehicleGuideEn: '4th house Taurus (Venus). Electric Blue or Metallic Silver pairs cutting-edge tech with smooth transit comfort.',
      vehicleGuideHi: 'चतुर्थ भाव वृषभ (शुक्र)। चमकीला नीला या सिल्वर रंग आधुनिक तकनीक व सुरक्षित यात्रा का संगम बनाता है।',
      vehicleSecretEn: 'Keep a small amethyst crystal or blue silk pouch in the glove compartment.',
      vehicleSecretHi: 'ग्लव बॉक्स में जामुनी कपड़ा या कटैला/स्फटिक रखने से विद्युत तंत्र की सुरक्षा रहती है।',

      walletRecEn: ['Midnight Blue', 'Charcoal Grey', 'Electric Blue Accents'],
      walletRecHi: ['मिडनाइट नीला', 'कोयला स्लेटी', 'चमकीला नीला'],
      walletAvoidEn: ['Blood Red'],
      walletAvoidHi: ['गहरा लाल'],
      walletGuideEn: 'Channels innovation royalties, tech investments, and visionary global gains.',
      walletGuideHi: 'तकनीकी निवेश, नवाचार व अप्रत्याशित स्रोतों से धन लाभ कराता है।',

      homeRecEn: ['Cool Grey', 'Crisp White', 'Soft Lavender', 'Pale Turquoise'],
      homeRecHi: ['हल्का स्लेटी', 'सफेद', 'हल्का जामुनी', 'फिरोजा'],
      homeAvoidEn: ['Heavy Maroon', 'Muddy Earth Tones'],
      homeAvoidHi: ['भारी मैरून', 'मटमैला रंग'],
      homeGuideEn: 'Open, airy, unconfined spaces that liberate thoughts and promote meditation.',
      homeGuideHi: 'खुले, हवादार और रचनात्मक वातावरण का निर्माण करता है जो मन को मुक्त करता है।',

      officeRecEn: ['Electric Blue Details', 'Glass & Aluminum', 'Cool White', 'Charcoal'],
      officeRecHi: ['नीला टच', 'कांच व एल्युमिनियम', 'सफेद', 'स्लेटी'],
      officeAvoidEn: ['Rustic Crimson'],
      officeAvoidHi: ['जंग लगा लाल'],
      officeGuideEn: 'Fosters unconventional breakthroughs, scientific research, and humanitarian projects.',
      officeGuideHi: 'वैज्ञानिक शोध, नए आविष्कार और वैश्विक सहयोग में सफलता दिलाता है।',

      brandRecEn: ['Electric Blue', 'Violet / Purple', 'Silver', 'Cyan'],
      brandRecHi: ['इलेक्ट्रिक ब्लू', 'बैंगनी', 'सिल्वर', 'आसमानी नीला'],
      brandAvoidEn: ['Dull Earthy Brown'],
      brandAvoidHi: ['धूमिल भूरा'],
      brandGuideEn: 'The hallmark of futuristic innovation, AI technologies, and social transformation.',
      brandGuideHi: 'भविष्यवादी नवाचार, आधुनिक तकनीक और सामाजिक परिवर्तन का संदेश देता है।',

      gadgetRecEn: ['Deep Blue / Titanium', 'Ultramarine Blue Case'],
      gadgetRecHi: ['गहरा नीला', 'अल्ट्रामैरीन ब्लू केस'],
      gadgetAvoidEn: ['Bright Crimson Case'],
      gadgetAvoidHi: ['उग्र लाल कवर'],
      gadgetGuideEn: 'Keeps neural circuits calm while managing high-bandwidth digital data.',
      gadgetGuideHi: 'डिजिटल उपकरणों पर काम करते समय दिमाग को शांत व सक्रिय रखता है।',

      sigRecEn: ['Royal Blue Ink', 'Dark Navy Ink'],
      sigRecHi: ['रॉयल ब्लू स्याही', 'गहरी नेवी स्याही'],
      sigAvoidEn: ['Red Ink'],
      sigAvoidHi: ['लाल स्याही'],
      sigGuideEn: 'Validates forward-thinking pacts, intellectual property, and institutional reform.',
      sigGuideHi: 'नवाचार व बौद्धिक संपदा के अनुबंधों को मजबूती और कानूनी बल देता है।'
    },
    Pisces: {
      vehicleRecEn: ['Golden Yellow', 'Pearl White', 'Lemon Yellow', 'Soft Champagne'],
      vehicleRecHi: ['सुनहरा पीला', 'मोती सफेद', 'हल्का पीला', 'शैंपेन'],
      vehicleAvoidEn: ['Jet Black', 'Dark Dull Navy', 'Charcoal'],
      vehicleAvoidHi: ['गहरा काला', 'गहरा नेवी ब्लू', 'कोयला स्लेटी'],
      vehicleGuideEn: '4th house Gemini (Mercury). Gold, Yellow or White keeps the journey blissful, intuition alert, and prevents mechanical delays.',
      vehicleGuideHi: 'चतुर्थ भाव मिथुन (बुध)। पीला, सफेद या शैंपेन रंग वाहन यात्रा को सुखद, अंतर्ज्ञान को सजग व सुरक्षित रखता है।',
      vehicleSecretEn: 'Keep a sandalwood piece or yellow pouch with a small silver fish emblem.',
      vehicleSecretHi: 'गाड़ी में चंदन का टुकड़ा या पीले कपड़े में चांदी का प्रतीक रखने से यात्रा कल्याणकारी रहती है।',

      walletRecEn: ['Turmeric Yellow', 'Rich Tan', 'Golden Amber'],
      walletRecHi: ['हल्दी पीला', 'टैन भूरा', 'सुनहरा अंबर'],
      walletAvoidEn: ['Jet Black', 'Lead Grey'],
      walletAvoidHi: ['गहरा काला', 'स्लेटी'],
      walletGuideEn: 'Attracts blessings-driven wealth, philanthropic capital, and creative royalty.',
      walletGuideHi: 'आशीर्वाद रूपी धन, रचनात्मक लाभ व ईश्वरीय कृपा से धन वृद्धि कराता है।',

      homeRecEn: ['Soft Sun Gold', 'Sea Pearl White', 'Pale Aqua', 'Warm Ivory'],
      homeRecHi: ['हल्का सुनहरा', 'सफेद', 'हल्का समुद्री हरा', 'आइवरी'],
      homeAvoidEn: ['Jet Black Accents', 'Dark Slates'],
      homeAvoidHi: ['काले शेड्स', 'गहरे स्लेटी'],
      homeGuideEn: 'A spiritual healing haven of boundless compassion, prayer, and dream tranquility.',
      homeGuideHi: 'घर में आध्यात्मिक शांति, पूजा, परोपकार और सुखद निद्रा का वास कराता है।',

      officeRecEn: ['Warm Maple Timber', 'Yellow Accents', 'Ivory White', 'Gold Trim'],
      officeRecHi: ['मेपल वुड', 'पीले पर्दे/टच', 'आइवरी सफेद', 'गोल्डन फिनिश'],
      officeAvoidEn: ['Dark Industrial Black'],
      officeAvoidHi: ['गहरा औद्योगिक काला'],
      officeGuideEn: 'Elevates counseling empathy, artistic genius, holistic medicine, and teaching.',
      officeGuideHi: 'परामर्श, चिकित्सा, कला, अध्यापन और रचनात्मक क्षेत्रों में सम्मान दिलाता है।',

      brandRecEn: ['Turmeric Gold', 'Aquamarine', 'Soft Lavender', 'Pure White'],
      brandRecHi: ['गोल्डन', 'एक्वामरीन नीला', 'हल्का लैवेंडर', 'शुद्ध सफेद'],
      brandAvoidEn: ['Harsh Black'],
      brandAvoidHi: ['कर्कश काला'],
      brandGuideEn: 'Exudes spiritual warmth, healing grace, imaginative beauty, and heartfelt integrity.',
      brandGuideHi: 'आध्यात्मिक शांति, स्वास्थ्य लाभ, कलात्मकता और सच्ची सेवा का विश्वास जगाता है।',

      gadgetRecEn: ['Starlight Gold', 'Soft Yellow / Cream Case', 'Pastel Turquoise'],
      gadgetRecHi: ['स्टारलाइट गोल्ड', 'क्रीम / हल्का पीला कवर', 'फिरोजा केस'],
      gadgetAvoidEn: ['Heavy Black Case'],
      gadgetAvoidHi: ['भारी काला कवर'],
      gadgetGuideEn: 'Filters out psychic pollution while navigating online streams.',
      gadgetGuideHi: 'डिजिटल माध्यमों पर काम करते समय नकारात्मक विचारों से रक्षा करता है।',

      sigRecEn: ['Royal Blue Fountain Ink', 'Saffron/Golden Pen Barrel with Blue Ink'],
      sigRecHi: ['रॉयल ब्लू फाउंटेन स्याही', 'स्वर्ण/पीले पेन से नीली स्याही'],
      sigAvoidEn: ['Black Ballpoint'],
      sigAvoidHi: ['काली बॉल पेन'],
      sigGuideEn: 'Endows covenants with spiritual sanctity, sincere integrity, and enduring blessings.',
      sigGuideHi: 'हस्ताक्षरित कार्यों में ईश्वरीय आशीर्वाद, ईमानदारी और स्थायी यश भरता है।'
    }
  };

  const item = DOMAIN_DATA[sign] || DOMAIN_DATA.Aries;

  return [
    {
      id: 'vehicle',
      iconName: 'Car',
      titleEn: 'Vehicle & Conveyance',
      titleHi: 'वाहन एवं कार रंग चयन',
      bhavaEn: '4th House (Vahan Bhava) & Venus',
      bhavaHi: 'चतुर्थ भाव (वाहन सुख) एवं शुक्र कारक',
      recommendedColorsEn: item.vehicleRecEn,
      recommendedColorsHi: item.vehicleRecHi,
      avoidColorsEn: item.vehicleAvoidEn,
      avoidColorsHi: item.vehicleAvoidHi,
      guidanceEn: item.vehicleGuideEn,
      guidanceHi: item.vehicleGuideHi,
      astrologicalSecretEn: item.vehicleSecretEn,
      astrologicalSecretHi: item.vehicleSecretHi
    },
    {
      id: 'wallet',
      iconName: 'Wallet',
      titleEn: 'Wallet & Cash Locker',
      titleHi: 'वॉलेट एवं धन तिजोरी रंग',
      bhavaEn: '2nd House (Dhana) & 11th House (Labha)',
      bhavaHi: 'द्वितीय भाव (धन संचय) एवं एकादश भाव (लाभ)',
      recommendedColorsEn: item.walletRecEn,
      recommendedColorsHi: item.walletRecHi,
      avoidColorsEn: item.walletAvoidEn,
      avoidColorsHi: item.walletAvoidHi,
      guidanceEn: item.walletGuideEn,
      guidanceHi: item.walletGuideHi,
      astrologicalSecretEn: 'Store a silver coin or unboiled rice in the currency pocket to anchor liquidity.',
      astrologicalSecretHi: 'वॉलेट के मुख्य पॉकेट में चांदी का छोटा सिक्का या साबुत अक्षत रखने से धन का अपव्यय रुकता है।'
    },
    {
      id: 'home',
      iconName: 'Home',
      titleEn: 'Home & Bedroom Interiors',
      titleHi: 'गृह व शयनकक्ष आंतरिक सज्जा',
      bhavaEn: '4th House (Griha Sukha) & Moon',
      bhavaHi: 'चतुर्थ भाव (गृह सुख) एवं चंद्र-शुक्र',
      recommendedColorsEn: item.homeRecEn,
      recommendedColorsHi: item.homeRecHi,
      avoidColorsEn: item.homeAvoidEn,
      avoidColorsHi: item.homeAvoidHi,
      guidanceEn: item.homeGuideEn,
      guidanceHi: item.homeGuideHi,
      astrologicalSecretEn: 'Avoid harsh stark contrasts on north/east master walls; prefer natural light pastel hues.',
      astrologicalSecretHi: 'उत्तर व पूर्व दिशा की दीवारों पर सौम्य पेस्टल रंग वास्तु अनुसार सुख-शांति बढ़ाते हैं।'
    },
    {
      id: 'office',
      iconName: 'Building2',
      titleEn: 'Workstation & Office Desk',
      titleHi: 'कार्यस्थल एवं ऑफिस टेबल',
      bhavaEn: '10th House (Karma) & 6th House (Service)',
      bhavaHi: 'दशम भाव (कर्म क्षेत्र) एवं षष्ठ भाव (दैनिक कार्य)',
      recommendedColorsEn: item.officeRecEn,
      recommendedColorsHi: item.officeRecHi,
      avoidColorsEn: item.officeAvoidEn,
      avoidColorsHi: item.officeAvoidHi,
      guidanceEn: item.officeGuideEn,
      guidanceHi: item.officeGuideHi,
      astrologicalSecretEn: 'Face your favorable compass direction and keep your primary lucky color stationery on your right.',
      astrologicalSecretHi: 'अपनी शुभ दिशा की ओर मुख करके बैठें और अपने दाहिने हाथ की ओर शुभ रंग की वस्तुएं रखें।'
    },
    {
      id: 'branding',
      iconName: 'Briefcase',
      titleEn: 'Business Brand & Logo',
      titleHi: 'व्यापार लोगो एवं ब्रांड रंग',
      bhavaEn: '10th House (Prestige) & 7th House (Trade)',
      bhavaHi: 'दशम भाव (कीर्ति) एवं सप्तम भाव (व्यापार साझीदारी)',
      recommendedColorsEn: item.brandRecEn,
      recommendedColorsHi: item.brandRecHi,
      avoidColorsEn: item.brandAvoidEn,
      avoidColorsHi: item.brandAvoidHi,
      guidanceEn: item.brandGuideEn,
      guidanceHi: item.brandGuideHi,
      astrologicalSecretEn: 'The primary logo color should match the 10th lord or 11th lord to attract loyal patrons.',
      astrologicalSecretHi: 'लोगो का मुख्य रंग दशमेश या एकादशेश के अनुसार रखने से बाजार में साख और ग्राहक आकर्षण बढ़ता है।'
    },
    {
      id: 'gadgets',
      iconName: 'Smartphone',
      titleEn: 'Phone Case & Gadgets',
      titleHi: 'स्मार्टफोन कवर व गैजेट्स',
      bhavaEn: '3rd House (Communication) & Rahu',
      bhavaHi: 'तृतीय भाव (संचार) एवं राहु-बुध तरंगें',
      recommendedColorsEn: item.gadgetRecEn,
      recommendedColorsHi: item.gadgetRecHi,
      avoidColorsEn: item.gadgetAvoidEn,
      avoidColorsHi: item.gadgetAvoidHi,
      guidanceEn: item.gadgetGuideEn,
      guidanceHi: item.gadgetGuideHi,
      astrologicalSecretEn: 'A phone case in your supporting color shields your personal aura from digital restlessness.',
      astrologicalSecretHi: 'शुभ रंग का मोबाइल कवर निरंतर संपर्क में रहने पर नकारात्मक स्क्रीन तनाव को कम करता है।'
    },
    {
      id: 'signature',
      iconName: 'PenTool',
      titleEn: 'Signature Pen & Ink',
      titleHi: 'हस्ताक्षर पेन एवं स्याही रंग',
      bhavaEn: '3rd House (Signatures) & 2nd House (Contracts)',
      bhavaHi: 'तृतीय भाव (हस्ताक्षर) एवं द्वितीय भाव (अनुबंध)',
      recommendedColorsEn: item.sigRecEn,
      recommendedColorsHi: item.sigRecHi,
      avoidColorsEn: item.sigAvoidEn,
      avoidColorsHi: item.sigAvoidHi,
      guidanceEn: item.sigGuideEn,
      guidanceHi: item.sigGuideHi,
      astrologicalSecretEn: 'Always use an upward-slanting signature with your auspicious ink to attract upward growth.',
      astrologicalSecretHi: 'शुभ स्याही से थोड़ा ऊपर की ओर उठते हुए हस्ताक्षर करने से उन्नति और यश की प्राप्ति होती है।'
    }
  ];
}

export const AuspiciousEssentialsBar: React.FC<AuspiciousEssentialsBarProps> = ({
  kundali,
  lang = 'en',
  onOpenNumerology,
  onOpenGemstones,
  className = '',
  variant = 'full'
}) => {
  const isHi = lang === 'hi';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeDomainId, setActiveDomainId] = useState<LifeDomainKey>('vehicle');

  // Listen to global event so any button in the app can open the modal
  useEffect(() => {
    const handleOpen = () => setIsModalOpen(true);
    window.addEventListener('astro_open_lucky_modal', handleOpen);
    return () => window.removeEventListener('astro_open_lucky_modal', handleOpen);
  }, []);

  // Compute Numerology data
  const birthDate = kundali.birthDetails?.date || '1990-01-01';
  const fullName = kundali.birthDetails?.name || 'Jataka';
  const gender = kundali.birthDetails?.gender || 'male';

  const numAnalysis: NumerologyAnalysis = useMemo(() => {
    return calculateNumerology(birthDate, fullName, gender);
  }, [birthDate, fullName, gender]);

  // Ascendant / Lagna Insights
  const lagnaHouse = kundali.charts?.D1?.houses?.find((h) => h.houseNumber === 1) || kundali.houses?.[0];
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const lagnaData = LAGNA_INSIGHTS[lagnaSign] || LAGNA_INSIGHTS.Aries;
  const lagnaContent = isHi ? lagnaData.hi : lagnaData.en;

  // Complete Color Taxonomy: Lucky, Neutral & Avoid
  const colorTaxonomy = useMemo(() => {
    return getColorTaxonomy(lagnaSign, numAnalysis.moolank, lang, lagnaContent.luckyColor);
  }, [lagnaSign, numAnalysis.moolank, lang, lagnaContent.luckyColor]);

  // Life Gemstone
  const lifeGem = kundali.gemstones?.find((g) => g.type === 'Life Stone (Lagna)') || kundali.gemstones?.[0] || {
    gemstone: isHi ? 'माणिक्य / पुखराज' : 'Yellow Sapphire / Pukhraj',
    substitute: 'Yellow Topaz',
    finger: 'Index Finger',
    metal: isHi ? 'स्वर्ण / पीतल' : 'Gold / Brass',
    wearingDay: isHi ? 'गुरुवार प्रातः' : 'Thursday morning'
  };

  // Auspicious Direction
  const lifeDomainAdvice = useMemo(() => {
    return getLifeDomainColorAdvice(lagnaSign, numAnalysis.moolank, lang);
  }, [lagnaSign, numAnalysis.moolank, lang]);

  const luckyDirection = useMemo(() => {
    return getAuspiciousDirection(lagnaSign, lang);
  }, [lagnaSign, lang]);

  const handleNavigateNumerology = () => {
    setIsModalOpen(false);
    if (onOpenNumerology) {
      onOpenNumerology();
    } else {
      window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'numerology' }));
    }
  };

  const handleNavigateGemstones = () => {
    setIsModalOpen(false);
    if (onOpenGemstones) {
      onOpenGemstones();
    } else {
      window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'gemstones' }));
    }
  };

  return (
    <>
      {/* =================================================================== */}
      {/* AUSPICIOUS ESSENTIALS BAR / CARD                                    */}
      {/* =================================================================== */}
      <div
        className={`bg-gradient-to-r from-amber-500/10 via-amber-50/40 to-emerald-500/10 dark:from-amber-950/20 dark:via-zinc-900 dark:to-emerald-950/20 border border-amber-300/60 dark:border-amber-700/50 rounded-2xl p-4 sm:p-5 shadow-xs relative overflow-hidden ${className}`}
      >
        {/* Decorative Top Accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-rose-400 to-emerald-400" />

        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-3 border-b border-amber-200/60 dark:border-amber-800/40">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-500/15 border border-amber-400/50 flex items-center justify-center shrink-0 shadow-2xs">
              <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm sm:text-base font-bold font-serif text-astro-text flex items-center gap-1.5">
                  <span>{isHi ? 'आपके शुभ तत्व व अंक' : 'Your Auspicious Elements & Numbers'}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 font-semibold border border-amber-300/60 dark:border-amber-700">
                    {isHi ? 'शुभ • तटस्थ • वर्जित रंग' : 'Lucky • Neutral • Avoid Colors'}
                  </span>
                </h3>
              </div>
              <p className="text-[11px] text-astro-secondary-text">
                {isHi
                  ? `मूलांक ${numAnalysis.moolank} (${numAnalysis.moolankLordHi}) एवं ${lagnaSign} लग्न के अनुसार सटीक वैदिक अंक, शुभ-तटस्थ-वर्जित रंग व दिशा`
                  : `Driver ${numAnalysis.moolank} (${numAnalysis.moolankLord}) & ${lagnaSign} Ascendant Harmonics (Lucky, Neutral & Avoid Colors)`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer hover:scale-102"
              title={isHi ? 'विस्तृत शुभ व वर्जित रंग विवरण देखें' : 'View Full Color & Number Guide'}
            >
              <Info className="w-3.5 h-3.5" />
              <span>{isHi ? 'संपूर्ण विवरण' : 'Full Guide'}</span>
            </button>
            <button
              type="button"
              onClick={handleNavigateNumerology}
              className="px-3 py-1.5 rounded-xl bg-white dark:bg-zinc-800 hover:bg-amber-50 dark:hover:bg-zinc-700 text-astro-text border border-amber-300/80 dark:border-amber-700/60 text-xs font-semibold flex items-center gap-1 shadow-2xs transition-all cursor-pointer"
              title={isHi ? 'अंक ज्योतिष चार्ट देखें' : 'View Numerology Tab'}
            >
              <Hash className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <span>{isHi ? 'अंक ज्योतिष' : 'Numerology'}</span>
              <ArrowRight className="w-3 h-3 text-astro-secondary-text" />
            </button>
          </div>
        </div>

        {/* 6-Column Auspicious Grid including Vehicle Colors */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3.5">
          {/* 1. LUCKY NUMBERS (शुभ अंक) */}
          <div
            onClick={() => setIsModalOpen(true)}
            className="bg-white/80 dark:bg-zinc-800/80 p-3 rounded-xl border border-amber-200/80 dark:border-amber-800/50 shadow-2xs hover:border-amber-400 transition-all cursor-pointer group"
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform" />
                {isHi ? 'शुभ अंक' : 'Lucky Numbers'}
              </span>
              <span className="text-[9px] text-amber-600 font-semibold opacity-70 group-hover:opacity-100">
                {isHi ? 'विवरण' : 'Info'}
              </span>
            </div>
            <div className="mt-1 flex items-baseline gap-1.5 flex-wrap">
              <span className="text-base font-extrabold text-amber-700 dark:text-amber-400">
                {numAnalysis.moolank}
              </span>
              <span className="text-[10px] text-astro-secondary-text">
                ({isHi ? 'मूलांक' : 'Driver'})
              </span>
              <span className="text-xs font-bold text-astro-text">/</span>
              <span className="text-base font-extrabold text-emerald-700 dark:text-emerald-400">
                {numAnalysis.bhagyank}
              </span>
              <span className="text-[10px] text-astro-secondary-text">
                ({isHi ? 'भाग्यांक' : 'Destiny'})
              </span>
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate mt-0.5">
              <span className="font-semibold text-astro-primary">{isHi ? 'मित्र अंक:' : 'Friendly:'}</span>{' '}
              {numAnalysis.luckyNumbers.join(', ')}
            </div>
          </div>

          {/* 2. COLOR HARMONY: LUCKY, NEUTRAL & AVOID (शुभ, तटस्थ व वर्जित रंग) */}
          <div
            onClick={() => setIsModalOpen(true)}
            className="bg-white/80 dark:bg-zinc-800/80 p-3 rounded-xl border border-rose-200/80 dark:border-rose-900/50 shadow-2xs hover:border-rose-400 transition-all cursor-pointer group"
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Palette className="w-3 h-3 text-rose-600 dark:text-rose-400 group-hover:scale-110 transition-transform" />
                {isHi ? 'रंग विश्लेषण' : 'Color Harmony'}
              </span>
              <div className="flex items-center gap-0.5">
                {colorTaxonomy.lucky.items.slice(0, 2).map((col, idx) => (
                  <span
                    key={`l-${idx}`}
                    className="w-2.5 h-2.5 rounded-full border border-black/20 inline-block shadow-2xs"
                    style={{ backgroundColor: col.hex }}
                    title={`Lucky: ${col.name}`}
                  />
                ))}
                <span className="w-1" />
                {colorTaxonomy.avoid.items.slice(0, 1).map((col, idx) => (
                  <span
                    key={`a-${idx}`}
                    className="w-2.5 h-2.5 rounded-full border border-rose-500 inline-block ring-1 ring-rose-400 shadow-2xs"
                    style={{ backgroundColor: col.hex }}
                    title={`Avoid: ${col.name}`}
                  />
                ))}
              </div>
            </div>
            {/* Lucky Line */}
            <div className="mt-1 text-xs font-bold text-astro-text truncate flex items-center gap-1">
              <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-extrabold shrink-0">
                {isHi ? 'शुभ:' : 'Lucky:'}
              </span>
              <span className="truncate">{colorTaxonomy.lucky.summary}</span>
            </div>
            {/* Neutral & Avoid Compact Summary */}
            <div className="text-[10px] text-astro-secondary-text truncate mt-0.5 flex items-center gap-1">
              <span className="text-rose-600 dark:text-rose-400 font-bold shrink-0">
                {isHi ? 'वर्जित:' : 'Avoid:'}
              </span>
              <span className="truncate text-rose-700 dark:text-rose-300 font-medium">
                {colorTaxonomy.avoid.summary}
              </span>
            </div>
          </div>

          {/* 3. VEHICLE & CAR COLOR (वाहन रंग) */}
          <div
            onClick={() => {
              setActiveDomainId('vehicle');
              setIsModalOpen(true);
            }}
            className="bg-white/80 dark:bg-zinc-800/80 p-3 rounded-xl border border-amber-300/80 dark:border-amber-700/50 shadow-2xs hover:border-amber-500 transition-all cursor-pointer group"
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Car className="w-3 h-3 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform" />
                {isHi ? 'वाहन रंग' : 'Vehicle Color'}
              </span>
              <span className="text-[9px] text-amber-600 font-semibold opacity-70 group-hover:opacity-100">
                {isHi ? '४था भाव' : '4th House'}
              </span>
            </div>
            <div className="mt-1 text-xs font-bold text-astro-text truncate flex items-center gap-1">
              <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-extrabold shrink-0">
                {isHi ? 'शुभ:' : 'Rec:'}
              </span>
              <span className="truncate">
                {(isHi ? lifeDomainAdvice[0]?.recommendedColorsHi : lifeDomainAdvice[0]?.recommendedColorsEn)?.slice(0, 2).join(', ')}
              </span>
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate mt-0.5 flex items-center gap-1">
              <span className="text-rose-600 dark:text-rose-400 font-bold shrink-0">
                {isHi ? 'वर्जित:' : 'Avoid:'}
              </span>
              <span className="truncate text-rose-700 dark:text-rose-300 font-medium">
                {(isHi ? lifeDomainAdvice[0]?.avoidColorsHi : lifeDomainAdvice[0]?.avoidColorsEn)?.slice(0, 1).join(', ')}
              </span>
            </div>
          </div>

          {/* 3. LUCKY GEMSTONE (भाग्य रत्न) */}
          <div
            onClick={handleNavigateGemstones}
            className="bg-white/80 dark:bg-zinc-800/80 p-3 rounded-xl border border-emerald-200/80 dark:border-emerald-900/50 shadow-2xs hover:border-emerald-400 transition-all cursor-pointer group"
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Gem className="w-3 h-3 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform" />
                {isHi ? 'भाग्य रत्न' : 'Lucky Gemstone'}
              </span>
              <ExternalLink className="w-2.5 h-2.5 text-emerald-600 opacity-70 group-hover:opacity-100" />
            </div>
            <div className="mt-1 text-xs font-bold text-emerald-800 dark:text-emerald-300 truncate">
              {lifeGem.gemstone}
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate mt-0.5">
              {lifeGem.metal || (isHi ? 'स्वर्ण / पीतल' : 'Gold / Brass')}
            </div>
          </div>

          {/* 4. FAVORABLE DAY (शुभ दिन) */}
          <div className="bg-white/80 dark:bg-zinc-800/80 p-3 rounded-xl border border-blue-200/80 dark:border-blue-900/50 shadow-2xs">
            <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400 flex items-center gap-1">
              <Calendar className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              {isHi ? 'शुभ दिन' : 'Favorable Day'}
            </div>
            <div className="mt-1 text-xs font-bold text-astro-text truncate">
              {lagnaContent.luckyDay || (isHi ? 'गुरुवार' : 'Thursday')}
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate mt-0.5">
              {isHi ? 'महत्वपूर्ण कार्य व अनुबंध' : 'Key Deals & Milestones'}
            </div>
          </div>

          {/* 5. AUSPICIOUS DIRECTION (शुभ दिशा) */}
          <div className="bg-white/80 dark:bg-zinc-800/80 p-3 rounded-xl border border-indigo-200/80 dark:border-indigo-900/50 shadow-2xs col-span-2 sm:col-span-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400 flex items-center gap-1">
              <Compass className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
              {isHi ? 'शुभ दिशा' : 'Lucky Direction'}
            </div>
            <div className="mt-1 text-xs font-bold text-indigo-800 dark:text-indigo-300 truncate">
              {luckyDirection}
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate mt-0.5">
              {isHi ? 'कार्य, अध्ययन व पूजा' : 'Work & Meditation'}
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* DETAILED AUSPICIOUS ESSENTIALS MODAL                                 */}
      {/* =================================================================== */}
      {isModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150"
        >
          <div className="bg-astro-bg border border-astro-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl space-y-5 p-5 sm:p-6 text-astro-text">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-astro-border pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold font-serif text-astro-text">
                    {isHi
                      ? 'संपूर्ण शुभ तत्व: अंक, शुभ रंग, तटस्थ रंग एवं वर्जित रंग'
                      : 'Complete Auspicious Essentials: Numbers, Lucky, Neutral & Avoid Colors'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? `${fullName} • जन्म तिथि: ${birthDate} • लग्न: ${lagnaSign} (${lagnaContent.archetype})`
                      : `${fullName} • Born: ${birthDate} • Lagna: ${lagnaSign}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-astro-secondary-text hover:text-astro-text hover:bg-astro-card border border-astro-border transition-colors cursor-pointer"
                title={isHi ? 'बंद करें' : 'Close'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Section 1: NUMEROLOGY CORE NUMBERS */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                  <Hash className="w-4 h-4 text-amber-600" />
                  {isHi ? 'अंक ज्योतिष विश्लेषण (मूलांक व भाग्यांक)' : 'Numerology Harmonization'}
                </h4>
                <button
                  type="button"
                  onClick={handleNavigateNumerology}
                  className="text-xs font-semibold text-astro-accent hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <span>{isHi ? 'विस्तृत अंक ज्योतिष खोलें' : 'Open Full Numerology'}</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Moolank Card */}
                <div className="bg-[#FAF6ED] dark:bg-zinc-800/90 p-4 rounded-xl border border-astro-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-amber-700 dark:text-amber-400">
                      {isHi ? 'मूलांक (Driver / Root)' : 'Root / Driver Number'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 font-semibold">
                      {isHi ? `शासक: ${numAnalysis.moolankLordHi}` : `Ruled by ${numAnalysis.moolankLord}`}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-amber-600 dark:text-amber-400 font-mono">
                      {numAnalysis.moolank}
                    </span>
                    <span className="text-xs text-astro-secondary-text">
                      {isHi ? numAnalysis.moolankTraits.titleHi : numAnalysis.moolankTraits.title}
                    </span>
                  </div>
                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {isHi
                      ? 'मूलांक आपके व्यक्तित्व, निर्णय लेने की स्वाभाविक शैली एवं मानसिक तरंगों का स्वामी है।'
                      : 'Represents your intrinsic nature, natural talents, and day-to-day decision-making frequency.'}
                  </p>
                </div>

                {/* Bhagyank Card */}
                <div className="bg-[#FAF6ED] dark:bg-zinc-800/90 p-4 rounded-xl border border-astro-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-emerald-700 dark:text-emerald-400">
                      {isHi ? 'भाग्यांक (Destiny / Life Path)' : 'Destiny / Life Path'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 font-semibold">
                      {isHi ? `शासक: ${numAnalysis.bhagyankLordHi}` : `Ruled by ${numAnalysis.bhagyankLord}`}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
                      {numAnalysis.bhagyank}
                    </span>
                    <span className="text-xs text-astro-secondary-text">
                      {isHi ? numAnalysis.bhagyankTraits.titleHi : numAnalysis.bhagyankTraits.title}
                    </span>
                  </div>
                  <p className="text-xs text-astro-secondary-text leading-relaxed">
                    {isHi
                      ? 'भाग्यांक आपके संपूर्ण जीवन का कर्म चक्र एवं भाग्य के उदय होने की दिशा को दर्शाता है।'
                      : 'Represents your ultimate karmic destiny, career culmination, and soul mission in this lifetime.'}
                  </p>
                </div>
              </div>

              {/* Number Harmony Quick Table */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-3.5 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                  <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 p-2.5 rounded-lg space-y-1">
                    <div className="font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1 text-[11px]">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      {isHi ? 'परम मित्र व शुभ अंक' : 'Most Harmonious Numbers'}
                    </div>
                    <div className="text-base font-extrabold text-emerald-700 dark:text-emerald-400 font-mono">
                      {numAnalysis.luckyNumbers.join(', ')}
                    </div>
                    <div className="text-[10px] text-astro-secondary-text">
                      {isHi ? 'वाहन, खाता, फोन व अनुबंध हेतु' : 'For phone, vehicles, contracts'}
                    </div>
                  </div>

                  <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 p-2.5 rounded-lg space-y-1">
                    <div className="font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1 text-[11px]">
                      <Sun className="w-3.5 h-3.5 text-amber-600" />
                      {isHi ? 'तटस्थ / सामान्य अंक' : 'Neutral Numbers'}
                    </div>
                    <div className="text-base font-extrabold text-amber-700 dark:text-amber-400 font-mono">
                      {numAnalysis.neutralNumbers?.join(', ') || (isHi ? '3, 9' : '3, 9')}
                    </div>
                    <div className="text-[10px] text-astro-secondary-text">
                      {isHi ? 'दैनिक सामान्य कार्यों हेतु' : 'For regular routine matters'}
                    </div>
                  </div>

                  <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 p-2.5 rounded-lg space-y-1">
                    <div className="font-bold text-rose-800 dark:text-rose-300 flex items-center gap-1 text-[11px]">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                      {isHi ? 'सावधानी वाले अंक' : 'Challenging Numbers'}
                    </div>
                    <div className="text-base font-extrabold text-rose-700 dark:text-rose-400 font-mono">
                      {numAnalysis.unluckyNumbers?.join(', ') || (isHi ? '8' : '8')}
                    </div>
                    <div className="text-[10px] text-astro-secondary-text">
                      {isHi ? 'अति महत्वपूर्ण कार्यों में टालें' : 'Avoid for critical milestones'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: COMPREHENSIVE COLOR TRIAD: LUCKY, NEUTRAL & AVOID */}
            <div className="space-y-4 pt-2 border-t border-astro-border">
              <div className="flex items-center justify-between">
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                  <Palette className="w-4 h-4 text-rose-600" />
                  {isHi ? 'रंग सामंजस्य त्रिकोण: शुभ, तटस्थ एवं वर्जित रंग' : 'Color Harmony Triad: Lucky, Neutral & Avoid Colors'}
                </h4>
                <span className="text-[10px] text-astro-secondary-text font-medium">
                  {isHi ? `आधार: ${lagnaSign} लग्न + मूलांक ${numAnalysis.moolank}` : `Based on: ${lagnaSign} + Moolank ${numAnalysis.moolank}`}
                </span>
              </div>

              {/* 3 Dedicated Color Category Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                {/* 1. LUCKY COLORS */}
                <div className="bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-300/80 dark:border-emerald-800/60 rounded-xl p-3.5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-extrabold uppercase text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      {isHi ? '१. शुभ रंग (Lucky)' : '1. Lucky Colors'}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-emerald-200/70 dark:bg-emerald-900/80 text-emerald-900 dark:text-emerald-200 font-bold">
                      {isHi ? 'सफलता दायक' : 'Auspicious'}
                    </span>
                  </div>

                  {/* Swatches */}
                  <div className="flex flex-wrap gap-1.5">
                    {colorTaxonomy.lucky.items.map((col, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-white dark:bg-zinc-800 border border-emerald-200 dark:border-emerald-800 shadow-2xs"
                      >
                        <span
                          className="w-3 h-3 rounded-full border border-black/20 shrink-0"
                          style={{ backgroundColor: col.hex }}
                        />
                        <span>{col.name}</span>
                      </span>
                    ))}
                  </div>

                  <div className="text-xs text-emerald-950 dark:text-emerald-100 font-medium leading-relaxed">
                    {colorTaxonomy.lucky.advice}
                  </div>

                  <div className="text-[11px] text-astro-secondary-text pt-1 border-t border-emerald-200/60 dark:border-emerald-800/40">
                    <span className="font-semibold text-emerald-800 dark:text-emerald-400">
                      {isHi ? 'प्रभाव:' : 'Effect:'}
                    </span>{' '}
                    {colorTaxonomy.lucky.rationale}
                  </div>
                </div>

                {/* 2. NEUTRAL COLORS */}
                <div className="bg-sky-50/70 dark:bg-sky-950/30 border border-sky-300/80 dark:border-sky-800/60 rounded-xl p-3.5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-extrabold uppercase text-sky-800 dark:text-sky-300 flex items-center gap-1.5">
                      <MinusCircle className="w-3.5 h-3.5 text-sky-600" />
                      {isHi ? '२. तटस्थ रंग (Neutral)' : '2. Neutral Colors'}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-sky-200/70 dark:bg-sky-900/80 text-sky-900 dark:text-sky-200 font-bold">
                      {isHi ? 'दैनिक संतुलन' : 'Everyday'}
                    </span>
                  </div>

                  {/* Swatches */}
                  <div className="flex flex-wrap gap-1.5">
                    {colorTaxonomy.neutral.items.map((col, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-white dark:bg-zinc-800 border border-sky-200 dark:border-sky-800 shadow-2xs"
                      >
                        <span
                          className="w-3 h-3 rounded-full border border-black/20 shrink-0"
                          style={{ backgroundColor: col.hex }}
                        />
                        <span>{col.name}</span>
                      </span>
                    ))}
                  </div>

                  <div className="text-xs text-sky-950 dark:text-sky-100 font-medium leading-relaxed">
                    {colorTaxonomy.neutral.advice}
                  </div>

                  <div className="text-[11px] text-astro-secondary-text pt-1 border-t border-sky-200/60 dark:border-sky-800/40">
                    <span className="font-semibold text-sky-800 dark:text-sky-400">
                      {isHi ? 'प्रभाव:' : 'Effect:'}
                    </span>{' '}
                    {colorTaxonomy.neutral.rationale}
                  </div>
                </div>

                {/* 3. AVOID COLORS */}
                <div className="bg-rose-50/80 dark:bg-rose-950/30 border border-rose-300/80 dark:border-rose-800/60 rounded-xl p-3.5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-extrabold uppercase text-rose-800 dark:text-rose-300 flex items-center gap-1.5">
                      <Ban className="w-3.5 h-3.5 text-rose-600" />
                      {isHi ? '३. वर्जित रंग (Avoid)' : '3. Colors to Avoid'}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-rose-200/80 dark:bg-rose-900/80 text-rose-900 dark:text-rose-200 font-bold">
                      {isHi ? 'सावधानी' : 'Caution'}
                    </span>
                  </div>

                  {/* Swatches with Strike/Border */}
                  <div className="flex flex-wrap gap-1.5">
                    {colorTaxonomy.avoid.items.map((col, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-white dark:bg-zinc-800 border border-rose-300 dark:border-rose-800 shadow-2xs ring-1 ring-rose-200 dark:ring-rose-900"
                      >
                        <span
                          className="w-3 h-3 rounded-full border border-rose-600 shrink-0 relative"
                          style={{ backgroundColor: col.hex }}
                        />
                        <span>{col.name}</span>
                      </span>
                    ))}
                  </div>

                  <div className="text-xs text-rose-950 dark:text-rose-100 font-medium leading-relaxed">
                    {colorTaxonomy.avoid.advice}
                  </div>

                  <div className="text-[11px] text-astro-secondary-text pt-1 border-t border-rose-200/60 dark:border-rose-800/40">
                    <span className="font-semibold text-rose-800 dark:text-rose-400">
                      {isHi ? 'कारण:' : 'Reason:'}
                    </span>{' '}
                    {colorTaxonomy.avoid.rationale}
                  </div>
                </div>
              </div>

              {/* Workaround / Practical Remedy Banner */}
              <div className="bg-amber-500/10 dark:bg-amber-950/20 border border-amber-300/80 dark:border-amber-700/60 rounded-xl p-3.5 flex items-start gap-2.5">
                <Shield className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs space-y-1">
                  <div className="font-bold text-amber-900 dark:text-amber-300">
                    {isHi ? '💡 यदि वर्जित रंग पहनना अनिवार्य हो (ड्रेस कोड / यूनिफॉर्म):' : '💡 If an Avoid Color is Mandatory (Dress Code / Uniform):'}
                  </div>
                  <p className="text-astro-secondary-text leading-relaxed">
                    {colorTaxonomy.remedyIfWorn}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3: DIRECTION, DAY & GEMSTONE */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-astro-border text-xs">
              {/* Gemstone */}
              <div className="bg-astro-card p-3 rounded-xl border border-astro-border space-y-1.5">
                <div className="font-bold text-emerald-700 dark:text-emerald-400 flex items-center justify-between">
                  <span className="flex items-center gap-1 text-[11px] uppercase">
                    <Gem className="w-3.5 h-3.5" />
                    {isHi ? 'भाग्य रत्न' : 'Auspicious Gem'}
                  </span>
                  <button
                    type="button"
                    onClick={handleNavigateGemstones}
                    className="text-[10px] text-emerald-600 underline cursor-pointer"
                  >
                    {isHi ? 'परामर्श' : 'Details'}
                  </button>
                </div>
                <div className="font-bold text-astro-text text-sm">{lifeGem.gemstone}</div>
                <div className="text-[10px] text-astro-secondary-text">{lifeGem.metal || 'Gold / Brass'}</div>
              </div>

              {/* Favorable Day */}
              <div className="bg-astro-card p-3 rounded-xl border border-astro-border space-y-1.5">
                <div className="font-bold text-blue-700 dark:text-blue-400 flex items-center gap-1 text-[11px] uppercase">
                  <Calendar className="w-3.5 h-3.5" />
                  {isHi ? 'अनुकूल वार' : 'Favorable Day'}
                </div>
                <div className="font-bold text-astro-text text-sm">{lagnaContent.luckyDay}</div>
                <div className="text-[10px] text-astro-secondary-text">
                  {isHi ? 'नया कार्य प्रारंभ करने हेतु' : 'Best for new beginnings'}
                </div>
              </div>

              {/* Lucky Direction */}
              <div className="bg-astro-card p-3 rounded-xl border border-astro-border space-y-1.5">
                <div className="font-bold text-indigo-700 dark:text-indigo-400 flex items-center gap-1 text-[11px] uppercase">
                  <Compass className="w-3.5 h-3.5" />
                  {isHi ? 'शुभ दिशा' : 'Lucky Direction'}
                </div>
                <div className="font-bold text-astro-text text-sm">{luckyDirection}</div>
                <div className="text-[10px] text-astro-secondary-text">
                  {isHi ? 'मुखाकृति: अध्ययन व साधना' : 'Facing direction for focus'}
                </div>
              </div>
            </div>

                        {/* Section 4: LIFE DOMAIN COLOURS (VEHICLE, WALLET, INTERIOR, BRAND, GADGETS, SIGNATURE) */}
            <div className="space-y-3.5 pt-2 border-t border-astro-border">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                  <Car className="w-4 h-4 text-amber-600" />
                  {isHi ? 'दैनिक जीवन व भौतिक सुख हेतु विशिष्ट रंग परामर्श' : 'Life Domain Color Guidance (Vehicles, Home & Daily Assets)'}
                </h4>
                <span className="text-[10px] text-astro-secondary-text">
                  {isHi ? 'वाहन (४था भाव) • धन (२/११ भाव) • कार्य (१०वां भाव)' : 'Vehicle (4th Bhava) • Dhana (2/11) • Career (10th)'}
                </span>
              </div>

              {/* Tabs for 7 domains */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                {lifeDomainAdvice.map((dom) => {
                  const isActive = activeDomainId === dom.id;
                  const IconComp = dom.id === 'vehicle' ? Car :
                    dom.id === 'wallet' ? Wallet :
                    dom.id === 'home' ? Home :
                    dom.id === 'office' ? Building2 :
                    dom.id === 'branding' ? Briefcase :
                    dom.id === 'gadgets' ? Smartphone : PenTool;

                  return (
                    <button
                      key={dom.id}
                      type="button"
                      onClick={() => setActiveDomainId(dom.id)}
                      className={'px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 flex items-center gap-1.5 transition-all cursor-pointer border ' + (
                        isActive
                          ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                          : 'bg-astro-card hover:bg-astro-bg text-astro-secondary-text hover:text-astro-text border-astro-border'
                      )}
                    >
                      <IconComp className="w-3.5 h-3.5" />
                      <span>{isHi ? dom.titleHi.split(' ')[0] : dom.titleEn.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Domain Detail Card */}
              {(() => {
                const cur = lifeDomainAdvice.find((d) => d.id === activeDomainId) || lifeDomainAdvice[0];
                const IconComp = cur.id === 'vehicle' ? Car :
                  cur.id === 'wallet' ? Wallet :
                  cur.id === 'home' ? Home :
                  cur.id === 'office' ? Building2 :
                  cur.id === 'branding' ? Briefcase :
                  cur.id === 'gadgets' ? Smartphone : PenTool;

                return (
                  <div className="bg-astro-card rounded-2xl border border-astro-border p-4 space-y-3.5 shadow-2xs animate-in fade-in duration-150">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2.5 border-b border-astro-border/70">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
                          <IconComp className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-xs sm:text-sm font-bold text-astro-text flex items-center gap-2 flex-wrap">
                            <span>{isHi ? cur.titleHi : cur.titleEn}</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-semibold border border-amber-200/60 dark:border-amber-800">
                              {isHi ? cur.bhavaHi : cur.bhavaEn}
                            </span>
                          </div>
                          <p className="text-[11px] text-astro-secondary-text mt-0.5">
                            {isHi ? cur.guidanceHi : cur.guidanceEn}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Recommended vs Avoid colors */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Recommended */}
                      <div className="bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 p-3 rounded-xl space-y-2">
                        <div className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          <span>{isHi ? 'अनुशंसित शुभ रंग (Recommended):' : 'Best Auspicious Colors:'}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(isHi ? cur.recommendedColorsHi : cur.recommendedColorsEn).map((col, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold bg-white dark:bg-zinc-800 border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100 shadow-2xs"
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full border border-black/20 shrink-0"
                                style={{ backgroundColor: getColorHex(col) }}
                              />
                              {col}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Avoid */}
                      <div className="bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 p-3 rounded-xl space-y-2">
                        <div className="text-xs font-bold text-rose-800 dark:text-rose-300 flex items-center gap-1.5">
                          <Ban className="w-3.5 h-3.5 text-rose-600" />
                          <span>{isHi ? 'वर्जित रंग (Colors to Avoid):' : 'Colors to Avoid:'}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(isHi ? cur.avoidColorsHi : cur.avoidColorsEn).map((col, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold bg-white dark:bg-zinc-800 border border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-100 shadow-2xs"
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full border border-rose-500 shrink-0"
                                style={{ backgroundColor: getColorHex(col) }}
                              />
                              {col}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Astrological Secret Remedy */}
                    <div className="bg-amber-500/10 dark:bg-amber-950/20 border border-amber-300/70 dark:border-amber-700/60 rounded-xl p-3 flex items-start gap-2.5 text-xs">
                      <Shield className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold text-amber-900 dark:text-amber-300">
                          {isHi ? 'विशेष वैदिक उपाय व सुरक्षा सूत्र: ' : 'Astrological Alignment & Secret: '}
                        </span>
                        <span className="text-astro-secondary-text">
                          {isHi ? cur.astrologicalSecretHi : cur.astrologicalSecretEn}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>


            {/* Footer Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-astro-border">
              <span className="text-[11px] text-astro-secondary-text text-center sm:text-left">
                {isHi
                  ? 'यह गणना महर्षि पराशर वैदिक ज्योतिष एवं अंक शास्त्र के त्रि-आयामी रंग सामंजस्य पर आधारित है।'
                  : 'Deterministic Triadic Color Harmonization based on Maharishi Parashari Jyotish & Chaldean Numerology.'}
              </span>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleNavigateNumerology}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-xl bg-astro-primary text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow-xs cursor-pointer hover:bg-astro-primary/90 transition-all"
                >
                  <Hash className="w-3.5 h-3.5" />
                  <span>{isHi ? 'पूर्ण अंक ज्योतिष पर जाएं' : 'Go to Full Numerology'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-astro-card hover:bg-astro-bg text-astro-text border border-astro-border text-xs font-semibold cursor-pointer transition-all"
                >
                  {isHi ? 'बंद करें' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
